/**
 * ARCHAEOLOGY — browser-verification history moved out of
 * `client/src/components/gags/CLAUDE.md` on 2026-09-23 to recover byte headroom
 * against that document's ceiling (`scripts/check-bloat.js`). The RULES these
 * sweeps were proving stay in the topic file; this is the dated "we checked and
 * here is what we checked" record, unbudgeted and beside the code.
 *
 * - **Increment 2 (drag-to-resize columns), 37 measured checks in a real
 *   browser against a copy of the dev database**: five group tables aligned to
 *   identical column edges before a drag, after a drag, after a reload, with
 *   every column at its floor, and with an editable row expanded; 1:1 pointer
 *   tracking both where the table stretches and where it overflows; the clamp
 *   measurably stopping every heading overflowing; both resets; both themes;
 *   and the drag/sort separation. jsdom can pin none of it.
 *
 * - **Cell-clipping fix, verified in a real browser against a copy of the dev
 *   database** (dept 213, five groups, 41 gags) at 1024/1280/2048, sidebar
 *   open AND collapsed, light AND dark: every cell's clip and ellipsis, the
 *   `title` tooltips, hit-testing that Title's paint stops at its own edge,
 *   all 12 row-edit controls unclipped and usable at the true floor widths
 *   (including `input type=date`), the kebab menu escaping the clip, the
 *   scenes picker, and Progress/Actions dragged to both extremes with Actions
 *   visible and hittable throughout — with the five group tables aligned to
 *   0.00px at every step. Measured with `elementFromPoint` and rects, never
 *   by eye.
 *
 * - **Increment 3 (frozen left-hand columns), verified in a real Chromium
 *   against a copy of the dev database (dept 213, five groups), 39 measured
 *   checks**, at 1280 and 2048, light AND dark, with `elementFromPoint`,
 *   element rects and clip-region screenshot comparison — never by eye.
 *   Covered: the chevron and Title holding their x at full right-scroll while
 *   column 3 moves 732px; every point across the frozen block hit-testing to
 *   a frozen cell in the resting, hovered and expanded states; the frozen
 *   fill equalling the row's in all four states in both themes; the dimmed
 *   row's frozen block painting byte-identically at scrollLeft 0 and at full
 *   right; the header corner holding both axes and hit-testing to itself; all
 *   five tables at identical offsets and widths; a 60px chevron resize moving
 *   Title's `left` 40 → 100 in every group with no gap or overlap; the page
 *   never scrolling sideways while the region does; the Scenes picker still
 *   escaping its cell; and the row editor inside the frozen Title cell still
 *   reachable.
 *
 * - **Hardening-pass browser sweep, 96 checks at 1024 AND 1280, both themes,
 *   all passing, plus 35 true-floor checks.** Own port, own chromium, own
 *   copy of the dev database (dept 213, five groups, 41 gags). Covered: five
 *   group tables aligned to 0.00px at defaults, at the true floors and after
 *   a drag; the page never scrolling sideways while the region does; no
 *   header spilling; all 75 resize handles present, opaque at rest and
 *   hittable; the frozen chevron and Title holding to within 1px at full
 *   right-scroll with 38/38 probes landing on a frozen cell and both cells
 *   opaque; the Filters panel unclipped and fully on screen; and — on the
 *   last row of the last group, the case the clipped-picker defect shipped
 *   in — all eleven row-edit controls sized and hittable, the Scenes trigger
 *   inside its column, and the portalled Scenes panel 5/5 reachable. Floors
 *   were set by writing the exact `min` map into `sfx_breakdown_col_widths`
 *   and reloading, which also exercises hydration.
 */
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BTN_XS, TAP_TARGET_PHONE } from '../../constants/buttons';
import {
  GAG_STATUS_LABELS,
  TASK_PRIORITY_LABELS,
  TASK_PRIORITY_COLORS,
  DAY_NIGHT_LABELS,
  INT_EXT_LABELS,
} from '../../constants/taskStatuses';
import { GAG_STATUS_COLORS, GAG_BUDGET_BADGES } from '../../constants/colors';
import { UserStatusIndicator } from '../UserAutocomplete';
import DueIndicator, { dueIndicatorTitle } from '../DueIndicator';
import { formatDate, formatDateCompact, toLocalISO } from '../../utils/dateFormat';
import { dueStatus } from '../../utils/dueStatus';
import { gagBudgetStatus } from '../../utils/gagBudgetStatus';
import { weekCommencingLabel } from '../../utils/ganttDates';
// ROW_HOVER is the shared row tint; the two frozen-cell constants beside it are
// its mirror ON THE CELL — a sticky <td> takes no background with it, so a
// frozen column has to restate every row state itself. The cascade reasoning
// (why the dark values are different Tailwind classes from the row's) is
// written out there, once.
import { ROW_HOVER, FROZEN_CELL_CLASS, ROW_BG_FROZEN_CELLS } from '../../constants/rowStyles';
import GagDetailPanel from './GagDetailPanel';
import ProgressBar from '../ProgressBar';
import useMeasuredWidth from '../../hooks/useMeasuredWidth';
import useMeasuredHeight from '../../hooks/useMeasuredHeight';
// THE sortable column header, shared with `components/OrderTableView.js`.
// ⚠️ The HEADER CELL is shared, never the `<table>`. ⚠️ **This comment said
// "that one is `table-fixed` with a `<colgroup>` of declared widths and this one
// is auto-layout with 15 columns" until the 2026-08-09 integration merge, and
// the second half is now FALSE of the file it is written in** — see the
// `table-fixed` + `<colgroup>` block further down, which this same branch added.
// The two tables are still not unifiable (12 vs 15 columns, one table vs N);
// since 2026-09-26 they share the SCROLL MODEL (🅱️, below) and the machinery
// behind it — `useLinkedScrollLeft`, `PAGE_SCROLL_PANE_X`, `ui/StickyScrollbar`.
import SortableHeader from '../ui/SortableHeader';
import StickyScrollbar from '../ui/StickyScrollbar';
import useLinkedScrollLeft from '../../hooks/useLinkedScrollLeft';
import useDragScroll from '../../hooks/useDragScroll';
// THE sticky-table-header treatment, shared with `components/OrderTableView.js`.
// The RULE it carries — a sticky header cannot pin to the page from inside a
// horizontally scrollable container — and the split-header arrangement (🅱️)
// both surfaces now use are written out there once. Never re-inline these
// strings.
import {
  PAGE_SCROLL_PANE_X,
  STICKY_REGION_CONTAINMENT,
  STICKY_SECTION_HEADER_Z,
  STICKY_COLUMN_HEADER_Z,
  STICKY_COLUMN_CELL_Z,
  STICKY_COLUMN_CORNER_Z,
  GAG_TABLE_HEADER_BG,
} from '../../constants/stickyTable';
// THE column model — headings, sort ids and declared widths, in one place.
// The widths are what make `table-fixed` possible, and `table-fixed` is what
// makes inline row editing survivable: a text input carries its own intrinsic
// minimum width, so controls in auto-layout cells resize the columns of
// whichever group you happen to be editing.
import {
  GAG_TABLE_COLUMNS,
  DEFAULT_GAG_COLUMN_WIDTHS,
  MAX_GAG_COLUMN_WIDTH,
  frozenGagColumnOffsets,
  boundFrozenColumnWidths,
} from '../../constants/gagTableColumns';
// THE grab strip on a header's right edge. It lives under `ui/` and knows
// nothing about gags; the reason it is a SIBLING of the sort button rather
// than a child of it — a drag that structurally cannot fire a sort — is
// written out in its own docblock and in `ui/SortableHeader` note 8.
import ColumnResizeHandle from '../ui/ColumnResizeHandle';
import { GAG_PHONE_TABLE_COLUMNS } from '../../constants/gagTableColumns';
import GagRowEditCell, {
  hasGagRowControl,
  gagRowControlledPanelFields,
  gagDraftFrom,
  buildGagDraftPatch,
  isGagDraftDirty,
  gagDraftSaveError,
  rebaseGagDraft,
  gagWithDraftApplied,
  panelFormFromDraft,
  draftPatchFromPanelForm,
} from './GagRowEditCell';
import { apiPut } from '../../utils/api';
// THE shared three-way confirm. It ALREADY carries `altLabel`/`onAlt` and
// `initialFocus`, added for the order pages' collapse prompt on 2026-08-08 —
// nothing here extends it, and a sixth hand-rolled modal would get the focus
// trap, Escape, overlay click and focus return subtly wrong.
import ConfirmDialog from '../ConfirmDialog';
import { UNSAVED_PROMPT_BUTTONS } from '../../constants/unsavedChangesPrompt';
import useDirtyContinuation from '../../hooks/useDirtyContinuation';
import GroupToggleLabel from '../ui/GroupToggleLabel';
import PageScrollTable from '../ui/PageScrollTable';
import { PHONE_FULL_BLEED_CARD } from '../../constants/phoneLayout';
import { statusPillClass } from '../ui/StatusPill';
import {
  ExpandChevron as RowExpandChevron,
  ExpandedPanelRow,
  EXPANDED_ROW_FRAME,
  ROW_EDIT_BAR_CLASS,
  ROW_EDIT_BAR_LABEL_CLASS,
  ROW_EDIT_UNSAVED_CHIP_CLASS,
  RowToggleChevron,
  KebabMenu,
  InlineEditPanelRow,
} from '../TableRowExpansion';

// ── The column model ────────────────────────────────────────
// 🔀 MOVED OUT 2026-08-09 to `constants/gagTableColumns.js`. The literal
// HEADERS array and HEADER_SORT map that used to live here are DERIVED from
// that one definition now, because a THIRD thing joined them — the declared
// px width — and the widths must be shared by every group table or the group
// cards stop aligning inside the shared scroll region. Read that file before
// touching a heading, a sort id or a width; the reasoning for all three is
// there, once.
//
// NB: the "Effect" (primary_effect_type) column is hidden from the table for
// now (Pete) to save horizontal space — the field is still editable in the
// detail panel + create modal and fully live server-side. To re-add: add its
// entry to GAG_TABLE_COLUMNS (label, sortId, width) and its row <td>, plus the
// FLAT_SORTS entry in constants/gagBreakdown.
const HEADERS = GAG_TABLE_COLUMNS.map((c) => c.label);

// 🔻 `HEADER_SORT` (a label → sortId map) was DELETED on 2026-08-09. The header
// row now iterates `GAG_TABLE_COLUMNS` itself rather than the `HEADERS` labels,
// because a header cell needs four things off the column — label, sortId, key
// and `min` (for the resize handle's `aria-valuemin`) — and looking three of
// them up by LABEL would make a duplicate heading string silently share a sort
// id and a width floor. `HEADERS` survives only as the `colSpan` count.
//
// The '' (expand chevron) and 'Actions' (kebab) columns still carry
// `sortId: null` and are still not sortable — they are CONTROLS, not data.

// Horizontal cell padding kept tight (px-2) so columns sit close together
// (Pete); vertical padding (py-2) unchanged to preserve row height / tap size.
//
// ═════════════════════════════════════════════════════════════════════════
// 🚨 `overflow-hidden` IS ON THE BASE CELL, SO **EVERY** CELL CLIPS — AND THE
// REASON IS A PREMISE THAT EXPIRED, NOT A NEW PREFERENCE. (2026-08-09)
// ═════════════════════════════════════════════════════════════════════════
// A `table-fixed` column has the width its `<col>` declares, but a CELL still
// paints its overflow **visibly** unless the cell itself clips. That never
// mattered here, and the reason it never mattered is exactly the thing that
// stopped being true: **every width in `constants/gagTableColumns.js` was
// MEASURED to fit its own content, so no cell could overflow the column it was
// sized for.** Spilling was unreachable, so only the two columns with values
// long enough to need an ellipsis (Scenes, Location) ever asked to clip.
//
// 🔀 **Drag-to-resize (increment 2) expired that premise.** The widths became
// DEFAULTS, and any column can now be dragged to its floor — which is the
// SORTABLE HEADER's floor, deliberately well below what the data needs, because
// data was expected to truncate. Product owner, 2026-08-09: *"When i change the
// size of title column (make it smaller). the contents does not shrink so the
// next column status over laps"*. Measured in Chromium at Title's floor (70px):
// `scrollWidth 141` against `clientWidth 70`, the text painting **70.63px past
// the cell's right edge**, straight across Status.
//
// 🔑 **THE RULE, not the instance: with user-resizable columns every cell must
// clip, because any column can now be dragged narrower than its content.** A
// new column inherits this by using `TD`; that is why the clip lives on the
// shared constant rather than being sprinkled per cell. Do not "tidy" it back
// onto individual cells — the next column added would silently miss it, and the
// symptom (text lying across its neighbour) reads as a width bug, not a missing
// utility class.
//
// ⚠️ `overflow-hidden` is the WHOLE treatment for a cell whose content is a
// control, a pill, a bar or an icon: `text-overflow` only acts on inline TEXT,
// so an ellipsis on a progress bar or a status pill would be inert decoration.
// Cells whose content is plain text take `TD_TEXT` instead, which adds the
// ellipsis — see the per-column decisions at the row's data cells below.
//
// 🪤 **`overflow: hidden` on a `<td>` is left undefined by CSS 2.1 but is
// honoured by every engine this app runs on — verified here rather than
// assumed**, because the Scenes and Location cells have relied on it (through
// `truncate`) since before this change and demonstrably clip.
//
// 🪤 **THE CLIP IS A SEPARATE TOKEN APPENDED TO `TD_UNCLIPPED`, NOT A CLASS
// LATER OVERRIDDEN BY `overflow-visible`.** Two conflicting Tailwind utilities
// on one element (`overflow-hidden overflow-visible`) resolve by STYLESHEET
// order, not by the order they are written in the string — the same hazard this
// repo already records for `mb-3 mb-2`. The one cell that must not clip
// therefore never receives the token in the first place.
const TD_UNCLIPPED = 'px-2 py-2 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap';
const TD = `${TD_UNCLIPPED} overflow-hidden`;
/**
 * The base cell plus an ellipsis, for cells whose content is PLAIN TEXT.
 *
 * 🔑 Split from `TD` rather than folded into it because `text-overflow` is
 * meaningless — and misleading to read — on the cells that hold a pill, a bar,
 * an icon or a `<select>`. Every cell clips; only text cells ellipse.
 *
 * ⚠️ **A cell that can ellipse needs a `title` so the full value stays
 * reachable on hover**, which is the treatment Supplier and Job Name already use
 * on the order table. The two go together: adding `TD_TEXT` to a cell whose
 * value is user-authored free text without also giving it a `title` hides data
 * with no way to get it back.
 */
const TD_TEXT = `${TD} text-ellipsis`;
/**
 * A text cell that does NOT clip — for the one cell whose editor opens an
 * in-flow popup. See the Scenes cell for the whole argument; built by ADDING to
 * the unclipped base rather than by overriding the clip, so no two conflicting
 * `overflow-*` utilities ever land on one element.
 */
const TD_TEXT_UNCLIPPED = `${TD_UNCLIPPED} text-ellipsis`;
/**
 * 🔀 THE EDIT-MODE FAMILY (owner 2026-09-29: *"on an expanded gag the main row
 * becomes editable. Can you make the gaps between each cell smaller maybe half
 * or less?"*). The row being edited inline takes `px-1` — 8px between two
 * controls instead of 16px — frozen expand/title cells included, so the row
 * stays consistent. Read-only rows, the header and every other row keep `px-2`.
 * 🪤 A SEPARATE LITERAL FAMILY, never `px-1` appended to a `px-2` string: two
 * conflicting padding utilities on one element resolve by stylesheet order.
 */
const TD_UNCLIPPED_EDIT = 'px-1 py-2 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap';
const TD_EDIT = `${TD_UNCLIPPED_EDIT} overflow-hidden`;
const TD_TEXT_EDIT = `${TD_EDIT} text-ellipsis`;
const TD_TEXT_UNCLIPPED_EDIT = `${TD_UNCLIPPED_EDIT} text-ellipsis`;
// 🔀 `TH_BASE` and `TH` DELETED 2026-08-08 — the header cell moved to
// `components/ui/SortableHeader`, which owns the heading type treatment AND the
// padding (the two differ by branch: `p-0` on a sortable `<th>` because its
// button carries `px-2 py-2`, and `px-2 py-2` on a non-sortable one). This
// file's copy of `TH_BASE` was byte-identical to the order table's apart from a
// leading `text-left`, which the shared cell now applies through its `align`
// prop — that near-duplicate was exactly the drift the owner asked to end.
// The `CI=true` build is what proves the bindings are really unused.

// ── Compact RA-status icons (table column only) ──────────────
// The gag detail panel keeps the fuller GAG_RA_BADGES text; the row column
// uses a single centred icon to save horizontal space. Driven by the same
// derived ra_status field ('required' | 'draft' | 'signed_off' | null).
function RaCheckIcon({ className }) {
  return (
    <svg className={`h-4 w-4 ${className}`} viewBox="0 0 20 20" fill="none" stroke="currentColor"
      strokeWidth={2.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 10.5l4 4 8-9" />
    </svg>
  );
}
function RaXIcon({ className }) {
  return (
    <svg className={`h-4 w-4 ${className}`} viewBox="0 0 20 20" fill="none" stroke="currentColor"
      strokeWidth={2.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 5l10 10M15 5L5 15" />
    </svg>
  );
}
const RA_ICONS = {
  signed_off: { title: 'Risk assessment signed off', el: <RaCheckIcon className="text-green-500 dark:text-green-400" /> },
  draft: { title: 'Risk assessment linked (draft)', el: <RaCheckIcon className="text-amber-500 dark:text-amber-400" /> },
  required: { title: 'Risk assessment required — none linked', el: <RaXIcon className="text-red-500 dark:text-red-400" /> },
};
function renderRaIcon(raStatus) {
  const entry = RA_ICONS[raStatus];
  if (!entry) {
    return (
      <span className="text-xs text-gray-400 dark:text-gray-500" title="No risk assessment required"
        aria-label="No risk assessment required">N/A</span>
    );
  }
  return (
    <span className="inline-flex items-center justify-center" title={entry.title} aria-label={entry.title}>
      {entry.el}
    </span>
  );
}

// ── Shared per-gag cell renderers ── used by the desktop table and the
// phone table (< md, 2026-10-03; replaced the card list).

// The `md` breakpoint (768px) expressed for matchMedia — must stay in step
// with the `hidden md:block` desktop table / `md:hidden` phone table.
// The .98 (Bootstrap convention) is load-bearing, NOT cosmetic: Tailwind's
// `md:` is `min-width: 768px`, so this query must be its exact complement.
// With a plain `767px` every width in (767, 768) — reachable via browser
// zoom, fractional DPR or Windows display scaling — satisfies NEITHER: CSS
// would show the phone table while isNarrow stayed false, mounting the panel
// inside the hidden <tr>, so a tap would expand it with no editor.
const NARROW_QUERY = '(max-width: 767.98px)';

// 📱 The phone table's column set + width key live in constants/gagTableColumns.js
// (GAG_PHONE_TABLE_COLUMNS) — a page that mocks this module still gets them.
/** The Shoot cell's tooltip lines: the due sentence, then the full date. */
const PHONE_TITLE_SEP = String.fromCharCode(10);

/** Shared pill chrome (status / priority / unassigned badges). */
// The shared status-pill shape + weight (ui/StatusPill): font-normal on a phone.
const PILL = `inline-flex ${statusPillClass()}`;

/**
 * £ budget-status glyph that sits beside a gag title.
 *
 * 🔑 **`shrink-0` lives HERE, in the component, not at the call site** — the
 * `components/OrderTableView.js` precedent, where the attachment marker owns its
 * own `flex-shrink-0`. In the table cell the glyph is a flex item beside a
 * shrinking title span, and a flex item shrinks by DEFAULT: without this the
 * title would eat the glyph at narrow widths instead of ellipsing.
 *
 * ⚠️ **It carries its own native `title`, which SHADOWS the cell's** — a `title`
 * on a child always wins over its parent's. That is deliberate and pre-existing:
 * the glyph means something different from the title it sits beside, and the
 * shadow is scoped to an 8px target. **Keep it a leaf** — putting the glyph
 * inside the truncating text span would both re-expose it to the ellipsis and
 * widen the shadowed area.
 */
function BudgetGlyph({ gag }) {
  // colour + tooltip from the shared helper/map (own title so it reads on hover).
  const budget = gagBudgetStatus({ estimatedCost: gag.estimated_cost, budgeted: gag.budgeted });
  return (
    <span className={`ml-1 shrink-0 font-semibold ${GAG_BUDGET_BADGES[budget.state].cls}`}
      title={budget.tooltip} aria-label={budget.tooltip}>£</span>
  );
}

/**
 * `fit` (table cells only, 2026-09-27): the pill never outgrows its cell and
 * its LABEL ellipses inside it, so a squeezed column keeps the colour and the
 * first letters rather than a pill sliced at the cell edge. The caller puts the
 * full label on the cell's `title`.
 */
function StatusPill({ status, fit = false }) {
  const text = GAG_STATUS_LABELS[status] || status;
  return (
    <span className={`${PILL} ${GAG_STATUS_COLORS[status] || GAG_STATUS_COLORS.todo}${fit ? ' max-w-full' : ''}`}>
      {fit ? <span className="truncate min-w-0">{text}</span> : text}
    </span>
  );
}

function PriorityPill({ priority, fit = false }) {
  const text = TASK_PRIORITY_LABELS[priority] || priority;
  return (
    <span className={`${PILL} ${TASK_PRIORITY_COLORS[priority] || TASK_PRIORITY_COLORS.medium}${fit ? ' max-w-full' : ''}`}>
      {fit ? <span className="truncate min-w-0">{text}</span> : text}
    </span>
  );
}

/**
 * Shoot date — the gag's effective deadline: a red/amber dot flags an overdue
 * / due-soon gag (shoot passed or within DUE_SOON_DAYS) that isn't
 * done/cancelled — shared with the task-board rows.
 *
 * 🔑 **THE DOT IS A DEADLINE SIGNAL, NOT AN OWNERSHIP ONE. Do not remove it as
 * a duplicate of the Owner column's "Unassigned" badge — that is a different
 * question and the two can be true independently.** This is written here
 * because that exact misreading was acted on: the owner asked for the colouring
 * to go on the grounds that *"the 'Unassigned' owner column highlights the gags
 * that are not assigned"*, and the predicate (`dueStatus`, `utils/dueStatus.js`)
 * reads only `shoot_date` and `status` — no owner field is involved anywhere. An
 * assigned gag whose shoot has passed is the commonest case worth flagging and
 * the one that would have gone silent.
 *
 * 🔻 **THE DATE TEXT IS PLAIN AS OF 2026-08-08 — the dot is the only carrier.**
 * It used to be `text-red-600`/`text-amber-600` + `font-semibold` as well.
 * Owner, having been shown what the colour actually meant: first *"keep the
 * colour but drop the bold"*, then a minute later *"actualy how aobut jst
 * keeping the colour dot?"* — one carrier instead of two saying the same thing.
 * ⚠️ The mobile CARD twin renders this same component, so it changed too; a
 * deadline that shouts on a phone and whispers on a desktop is the
 * inconsistency, not the fix.
 *
 * 🔀 **`titleOnParent` (2026-08-08) — THE TABLE'S TOOLTIP LIVES ON THE `<td>`,
 * THE CARD'S STAYS ON THE DOT.** Product owner: *"dot tool tips like the amber
 * dots have a tool tip but you have to hover over the dot. please make it the
 * whole cell that has the same tool tip"*. An 8×8px hover target is a bad one, so
 * the desktop row hands the same string to the whole cell (see `shootDateTooltip`
 * and the `<td>` that uses it) and passes `titleOnParent` so the dot stops
 * setting its own — **a child's `title` shadows its parent's**, so keeping both
 * would give the dot's text over the dot and the cell's text everywhere else,
 * which is two tooltips again.
 * 📱 The PHONE table's shoot cell (2026-10-03) follows the same rule: tooltip
 * on the `<td>`, `titleOnParent` on the dot.
 */
function ShootDate({ gag, titleOnParent = false, fit = false }) {
  const shootDue = dueStatus(gag.shoot_date, gag.status);
  // `fit` (table cell only, 2026-09-27): the DATE ellipses, the dot never does
  // (`DueIndicator` is `flex-shrink-0`); the cell's `title` carries the date.
  return (
    <span className={`inline-flex items-center gap-1.5${fit ? ' max-w-full' : ''}`}>
      <DueIndicator state={shootDue} titleOnParent={titleOnParent} />
      {fit ? <span className="truncate min-w-0">{formatDate(gag.shoot_date)}</span> : formatDate(gag.shoot_date)}
    </span>
  );
}

/**
 * The shoot-date CELL's tooltip — the same sentence the dot used to carry, on a
 * target the width of the column.
 *
 * 🔑 **It is `dueIndicatorTitle`'s string verbatim, not a second wording.** The
 * owner asked for "the same tool tip", and one definition in `DUE_CONFIG` is what
 * makes that true a year from now. It is deliberately NOT enriched with the shoot
 * date the way the order table's `buildOrderDateTooltip` is: that cell holds two
 * dates and had a raw-ISO defect to fix, whereas this one renders its single date
 * in plain legible text an inch from the pointer, so repeating it would be noise.
 * ⚠️ The CALLER may still append the date — see `shootCellTitle` (2026-09-27).
 * 🔴 **No due state → NO ATTRIBUTE.** A gag whose shoot is comfortably ahead has
 * no dot and had no tooltip; it still has none, rather than a blank tooltip box.
 * ⚠️ Returns `undefined`, never `''` — `title=""` renders an EMPTY attribute and
 * some browsers show an empty box for it.
 */
function shootDateTooltip(gag) {
  return dueIndicatorTitle(dueStatus(gag.shoot_date, gag.status));
}

/**
 * Owner + lifecycle indicator, or the Unassigned badge.
 *
 * 🚨 **THE CSS TRUNCATION HAD TO GO ON THE NAME SPAN, NOT ON THE `<td>`, AND
 * ONLY THIS SHAPE WORKS HERE.** This is an `inline-flex` of the lifecycle
 * indicator plus the name, so `text-overflow` on any ancestor is INERT — it acts
 * on inline text, and the cell's direct content is a flex container. Left on the
 * cell it would also clip the INDICATOR away before it touched the name, which
 * is the lesson `components/OrderTableView.js`'s Ordered By cell records at
 * length. So: `truncate min-w-0` on the text span (🪤 **`min-w-0` is
 * load-bearing — a flex item's default `min-width: auto` pins it at its
 * intrinsic content width and it will not shrink at all**), and a
 * `flex-shrink-0` wrapper on the indicator so a long name cannot squeeze it out.
 *
 * ⚠️ The wrapper is here rather than inside `UserStatusIndicator` deliberately:
 * that component is shared by the crew table, the Gantt labels, the Users table
 * and `UserAutocomplete`, and it takes no `className`. The flex context belongs
 * to this caller, so the shrink rule does too.
 *
 * The full name stays in the title/aria tooltip and drives the owner sort
 * (the comparator reads gag.owner_name).
 */
/**
 * The gag as the read-only Owner cell should show it while a row is being
 * edited: the shared draft's owner (2026-09-29), named from the department
 * list. Unchanged owner → the gag itself, so its status indicator survives.
 */
function pendingOwnerGag(gag, draft, deptUsers) {
  const current = gag.owner_id == null ? '' : String(gag.owner_id);
  if (!draft || draft.owner === current) return gag;
  if (draft.owner === '') return { ...gag, owner_id: null, owner_name: null, owner_status: null };
  const u = (deptUsers || []).find((x) => String(x.id) === draft.owner);
  return { ...gag, owner_id: Number(draft.owner), owner_name: u?.display_name || null, owner_status: u?.status ?? null };
}

function OwnerLabel({ gag, fit = false }) {
  if (gag.owner_id == null) {
    // `fit` (table cell only, 2026-09-27): at the 60–70px an Owner column is
    // squeezed to, "Unassigned" ellipses inside its pill and says itself on
    // hover — measured, it was otherwise sliced at the cell edge with nothing
    // to recover it.
    return (
      <span
        className={`${PILL} bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300${fit ? ' max-w-full' : ''}`}
        title={fit ? 'Unassigned' : undefined}
      >
        {fit ? <span className="truncate min-w-0">Unassigned</span> : 'Unassigned'}
      </span>
    );
  }
  const fullName = gag.owner_name || `User ${gag.owner_id}`;
  return (
    <span className="inline-flex items-center gap-1.5 min-w-0 max-w-full" title={fullName} aria-label={fullName}>
      {/* Lifecycle dot/icon (active/invited/placeholder/deactivated) —
          renders nothing on unknown status */}
      <span className="flex-shrink-0 inline-flex items-center">
        <UserStatusIndicator status={gag.owner_status} />
      </span>
      <span className="truncate min-w-0">{fullName}</span>
    </span>
  );
}

/**
 * Task-completion bar + compact d/t count. The "has no tasks" RULE lives here
 * and nowhere else — callers only choose what an empty gag renders instead
 * (`empty`): the table column wants a '-' placeholder to keep the cell
 * occupied, the card wants nothing at all rather than a stray indented row.
 * Callers must NOT pre-test task_total themselves, or the rule drifts.
 */
function GagProgress({ gag, barClassName, empty = '-' }) {
  if (!(gag.task_total > 0)) return empty;
  return (
    <span className="inline-flex items-center gap-1.5">
      <ProgressBar done={gag.task_done} total={gag.task_total} className={barClassName} />
      <span className="text-xs text-gray-500 dark:text-gray-400">{gag.task_done}/{gag.task_total}</span>
    </span>
  );
}

/**
 * Expand/collapse chevron for a gag, in either twin.
 *
 * A real <button> owns the toggle so keyboard users get Enter/Space + focus
 * for free (mirrors the Teams page's aria-expanded pattern); the row/card
 * around it stays clickable for mouse, and stopPropagation keeps this click
 * from also firing that handler (no double-toggle). onToggle is toggleRow,
 * which carries the dirty guard, so keyboard activation respects it too.
 *
 * `onToggle` omitted = a non-interactive gag (the deleted view, where editing
 * would 409 server-side): renders the muted static glyph instead.
 */
function ExpandChevron({ expanded, onToggle, className = '' }) {
  // 🔀 2026-10-25: the body is the shared `RowToggleChevron`
  // (TableRowExpansion.js), moved VERBATIM; only the noun is the gag's.
  // ONE wording for both twins — the accessible name must not depend on
  // which breakpoint the user happens to be on.
  return (
    <RowToggleChevron
      expanded={expanded}
      onToggle={onToggle}
      className={className}
      expandLabel="Expand gag"
      collapseLabel="Collapse gag"
    />
  );
}

/**
 * The element carrying `rowKey`, for the deep-expand scroll + the
 * collapse keep-in-view logic. The desktop <tr> and (below md) the phone
 * table row both carry data-rowkey — prefer the laid-out one. offsetParent is
 * null for a display:none element (and for every element under jsdom, which
 * has no layout — hence the fallback to the first match).
 */
function findRowEl(rowKey) {
  const els = [...document.querySelectorAll('[data-rowkey]')]
    .filter((el) => el.getAttribute('data-rowkey') === rowKey);
  return els.find((el) => el.offsetParent !== null) || els[0] || null;
}

/** Monday of the week containing the given YYYY-MM-DD date string. */
function mondayOf(dateStr) {
  const d = new Date(String(dateStr).split('T')[0] + 'T00:00:00');
  if (isNaN(d.getTime())) return null;
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); // getDay(): Sun=0 … Sat=6
  return d;
}

// Week-commencing label ("w/c Mon 3 Aug") is shared — see ganttDates.weekCommencingLabel.

// ── Sort comparators — client-side over the fetched list. Shared by flat
// mode AND the grouped modes (which sort rows WITHIN each group). Direction
// flips the primary comparison; a stable ascending title tiebreak keeps the
// order deterministic and readable. Enum orders come from the shared
// constants (urgent-first / status pipeline). NULLs sort last in ascending —
// ⚖️ EXCEPT `shoot`, which sorts them FIRST by owner ruling (2026-09-22); the
// reasoning is at `nullsFirst` below and the ordering is pinned by
// `GagGroupTable.shootSort.test.js`.
const PRIORITY_ORDER = ['urgent', 'high', 'medium', 'low'];
const STATUS_ORDER = ['todo', 'in_progress', 'blocked', 'ready', 'done', 'cancelled'];
const RA_ORDER = ['required', 'draft', 'signed_off']; // null (no RA required) sorts last
const byTitle = (a, b) => String(a.title || '').localeCompare(String(b.title || ''));
const cmpStr = (a, b) => String(a).localeCompare(String(b));
/** Position of v in an ordered enum list; unknown/absent values sort last. */
const orderOf = (list, v) => { const i = list.indexOf(v); return i === -1 ? list.length : i; };
/** NULLs-last comparison of two nullable numbers/dates; 0 when tied. */
function nullsLast(a, b) {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return a < b ? -1 : a > b ? 1 : 0;
}
/**
 * NULLs-**FIRST** comparison of two nullable values; 0 when tied.
 *
 * ⚖️ **ONE COLUMN USES THIS — `shoot` — BY OWNER RULING, 2026-09-22.** His
 * order, top to bottom: *no shoot date → past → today → tomorrow → … → latest
 * at the bottom*. Every other nullable column on this table is still
 * `nullsLast`/`nullsLastBy`, and that asymmetry is the decision, not an
 * oversight — **do not "restore consistency" by unifying the two helpers.**
 *
 * 🔑 Why this column is the exception: it is the one the page OPENS on
 * (`DEFAULT_FLAT_SORT = 'shoot'`, `DEFAULT_FLAT_SORT_DIR = 'asc'`), and an
 * undated gag is unscheduled work still to be planned — the thing most worth
 * seeing — rather than finished business. Below three months of shot days it
 * is invisible.
 *
 * 🟢 Descending needs no second branch and must not grow one: `makeComparator`
 * multiplies the primary by −1, so nulls-first ascending IS nulls-last
 * descending for free. Pinned by `GagGroupTable.shootSort.test.js` §2.
 */
function nullsFirst(a, b) {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  return a < b ? -1 : a > b ? 1 : 0;
}
/** NULLs-last comparison delegating to cmp when both sides are present. */
function nullsLastBy(a, b, cmp) {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return cmp(a, b);
}
/** Earliest scene_sort across a gag's scenes (null when none / all unsorted). */
function sceneKey(g) {
  if (!g.scenes || !g.scenes.length) return null;
  let best = null;
  for (const s of g.scenes) {
    if (s.scene_sort == null) continue;
    if (best == null || s.scene_sort < best) best = s.scene_sort;
  }
  return best;
}
/** Task completion ratio (null when the gag has no tasks → sorts last). */
const progressKey = (g) => (g.task_total > 0 ? g.task_done / g.task_total : null);

// Primary comparators (ascending, no tiebreak) keyed by sort-column id.
const PRIMARY_COMPARATORS = {
  title: (a, b) => byTitle(a, b),
  status: (a, b) => orderOf(STATUS_ORDER, a.status) - orderOf(STATUS_ORDER, b.status),
  ra: (a, b) => orderOf(RA_ORDER, a.ra_status) - orderOf(RA_ORDER, b.ra_status),
  sub_department: (a, b) => nullsLastBy(a.sub_department_name, b.sub_department_name, cmpStr),
  scene: (a, b) => nullsLast(sceneKey(a), sceneKey(b)),
  // ⚖️ `nullsFirst`, NOT `nullsLast` — the one exception on this table, owner
  // ruling 2026-09-22. Read the helper's docblock before changing it back.
  shoot: (a, b) => nullsFirst(a.shoot_date, b.shoot_date),
  unit: (a, b) => nullsLastBy(a.unit, b.unit, cmpStr),
  effect: (a, b) => nullsLastBy(a.primary_effect_type, b.primary_effect_type, cmpStr),
  day_night: (a, b) => nullsLastBy(a.day_night, b.day_night, cmpStr),
  int_ext: (a, b) => nullsLastBy(a.int_ext, b.int_ext, cmpStr),
  location: (a, b) => nullsLastBy(a.location, b.location, cmpStr),
  owner: (a, b) => nullsLastBy(a.owner_name, b.owner_name, cmpStr),
  priority: (a, b) => orderOf(PRIORITY_ORDER, a.priority) - orderOf(PRIORITY_ORDER, b.priority),
  progress: (a, b) => nullsLast(progressKey(a), progressKey(b)),
};

/** Build a full comparator (primary × direction, ascending-title tiebreak). */
function makeComparator(col, dir) {
  const primary = PRIMARY_COMPARATORS[col] || PRIMARY_COMPARATORS.shoot;
  const sign = dir === 'desc' ? -1 : 1;
  return (a, b) => (primary(a, b) * sign) || byTitle(a, b);
}

/**
 * Bucket gags into ordered groups for the active group-by mode.
 * Returns [{ key, label, gags }]. Keys are prefixed per mode so expansion
 * state is tracked independently across modes.
 * 🔻 That state was PERSISTED until 2026-09-22 (a localStorage key of its own);
 * it is session state now — owner ruling, see the no-persistence block at the
 * top of `pages/GagBreakdown.js`. The per-mode prefix still earns its keep:
 * switching grouping inside one session must not carry an expansion from a
 * different mode's key space.
 */
function buildGroups(gags, groupBy, sortCol, sortDir) {
  const cmp = makeComparator(sortCol, sortDir);
  // Sort rows WITHIN each group by the shared sort state; the group ORDER
  // (built below per mode) is left untouched.
  const sorted = (groups) => { groups.forEach((g) => g.gags.sort(cmp)); return groups; };

  if (groupBy === 'all') {
    // Flat mode: ONE continuous pseudo-group (label null → no header row,
    // never collapsible), locally re-sorted by the shared sort state.
    return [{ key: 'all', label: null, gags: [...gags].sort(cmp) }];
  }

  if (groupBy === 'shoot_week') {
    const weeks = new Map(); // iso monday -> { key, label, gags }
    const undated = [];
    for (const g of gags) {
      const monday = g.shoot_date ? mondayOf(g.shoot_date) : null;
      if (!monday) { undated.push(g); continue; }
      const iso = toLocalISO(monday);
      if (!weeks.has(iso)) weeks.set(iso, { key: `week:${iso}`, label: weekCommencingLabel(monday), gags: [] });
      weeks.get(iso).gags.push(g);
    }
    const groups = [...weeks.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([, grp]) => grp);
    if (undated.length) groups.push({ key: 'week:none', label: 'No shoot date', gags: undated });
    return sorted(groups);
  }

  if (groupBy === 'scene') {
    // A gag with multiple scenes appears under EACH of its scenes — that is
    // the point of scene grouping. No-scene gags land in "No scene" at the end.
    const scenes = new Map(); // lowercase scene -> { sort, label, gags }
    const noScene = [];
    for (const g of gags) {
      if (!g.scenes || g.scenes.length === 0) { noScene.push(g); continue; }
      for (const s of g.scenes) {
        const k = String(s.scene_number).toLowerCase();
        if (!scenes.has(k)) scenes.set(k, { sort: s.scene_sort, label: `Scene ${s.scene_number}`, gags: [] });
        scenes.get(k).gags.push(g);
      }
    }
    const groups = [...scenes.entries()]
      .sort((a, b) => {
        const [ka, va] = a; const [kb, vb] = b;
        if (va.sort != null && vb.sort != null && va.sort !== vb.sort) return va.sort - vb.sort;
        if (va.sort != null && vb.sort == null) return -1;
        if (va.sort == null && vb.sort != null) return 1;
        return ka.localeCompare(kb);
      })
      .map(([k, v]) => ({ key: `scene:${k}`, label: v.label, gags: v.gags }));
    if (noScene.length) groups.push({ key: 'scene:none', label: 'No scene', gags: noScene });
    return sorted(groups);
  }

  if (groupBy === 'assignment') {
    const unassigned = gags.filter((g) => g.owner_id == null);
    const assigned = gags.filter((g) => g.owner_id != null);
    const groups = [];
    if (unassigned.length) groups.push({ key: 'assign:unassigned', label: 'Unassigned', gags: unassigned });
    if (assigned.length) groups.push({ key: 'assign:assigned', label: 'Assigned', gags: assigned });
    return sorted(groups);
  }

  // Default: sub-department. Server order already sorts named sub-depts first.
  const subs = new Map();
  for (const g of gags) {
    const key = g.sub_department_id != null ? `sub:${g.sub_department_id}` : 'sub:none';
    const label = g.sub_department_name || 'No sub-department';
    if (!subs.has(key)) subs.set(key, { key, label, gags: [] });
    subs.get(key).gags.push(g);
  }
  const groups = [...subs.values()];
  // Keep "No sub-department" last (server already orders nulls last, but be explicit).
  groups.sort((a, b) => (a.key === 'sub:none') - (b.key === 'sub:none'));
  return sorted(groups);
}

/**
 * Grouped, collapsible master breakdown table. Groups are collapsed by
 * default and rows render ONLY when their group is expanded — that is the v1
 * scale strategy (no virtualization dependency).
 *
 * Props:
 *  - gags, groupBy ('sub_department'|'shoot_week'|'scene'|'assignment')
 *  - expandedGroups: { [groupKey]: bool }, onToggleGroup(key)
 *  - canManage: manager rights in this department (drives Assign/Release/hard delete)
 *  - userId: current user id (owner-only actions)
 *  - deletedView: rendering the ?deleted=1 list (Restore / Delete permanently)
 *  - deptUsers: [{ id, display_name, status }] for the Assign picker
 *    (from /api/gags/assignable-users — same membership the assign
 *    validator accepts, so a picked user can never 400 on dept grounds)
 *  - subDepts: [{ id, name }] passed through to the detail panel's selects
 *  - sceneSuggestions: OPTIONAL [{ scene_number, gag_count }] from
 *    /api/gags/scene-suggestions — the scene numbers already used by gags in
 *    this department, which are the ONLY possible options for the inline
 *    Scenes multi-select (there is no master scene table). Omit it and the
 *    picker still works: it offers the gag's own scenes plus anything typed.
 *  - onGagUpdated: silent list refetch after a panel save (row columns
 *    otherwise go stale)
 *  - onAssign(gag, ownerId) / onRelease / onSoftDelete / onRestore / onHardDelete
 *    (🔻 `onTake` DELETED 2026-10-02 with the kebab's Take — see the kebab)
 *  - onDirtyStateChange(bool): OPTIONAL — reports whether any expanded row's
 *    panel has unsaved edits. The Table→Timeline view switch lives in
 *    GagBreakdown, above this component, and must not silently unmount a
 *    dirty panel — same lift as GagDetailPanel→here, one level further up.
 *  - dirtyWarnSignal: OPTIONAL counter — each bump fires the amber pulse.
 *    🔻 GagBreakdown stopped passing it 2026-09-29 (a blocked view switch now
 *    ASKS via continueWhenClean); kept for any caller that still pulses.
 *  - flatSort: active sort-column id (shared with the flat-mode Sort select).
 *    Drives flat-mode order AND within-group order in the grouped modes.
 *  - sortDir: 'asc' | 'desc' for the active sort column.
 *  - onHeaderSort(colId): a data header was clicked — the page toggles
 *    direction when colId is already active, else selects it ascending, and
 *    persists both (keeping the Sort select in sync).
 */
export default function GagGroupTable({
  gags,
  groupBy,
  expandedGroups,
  onToggleGroup,
  canManage,
  userId,
  deletedView,
  deptUsers,
  subDepts,
  sceneSuggestions,
  onGagUpdated,
  onAssign,
  onRelease,
  onSoftDelete,
  onRestore,
  onHardDelete,
  onDirtyStateChange,
  dirtyWarnSignal,
  // OPTIONAL counter (owner 2026-10-05): each bump CLOSES the open row. The page
  // bumps it on a status-tab tap, after asking about unsaved work. Seeded from
  // its MOUNT value, so a remount with a non-zero counter closes nothing.
  collapseAllSignal,
  /**
   * ── Ask-then-continue (owner 2026-09-29) — `hooks/useDirtyContinuation.js`.
   * The PAGE owns the hook (its `dirty` is the page's own flag, so a released
   * route change runs only once the leave guard is disarmed — no double
   * prompt) and hands down all three. Omitted, this table runs its OWN copy
   * over `panelDirty`, so a bare `<GagGroupTable>` still asks-then-continues.
   *  - continueWhenClean(action?): run now if clean, else hold + prompt
   *  - collapseRequestSignal: bumped counter → open the three-way prompt
   *  - onCollapsePromptResolved('kept'|'discarded'|'saved'|'failed')
   *  - onLinkNavigate(to): OPTIONAL — in-panel app links ask first (the
   *    expanded GagDetailPanel intercepts them); omitted, links behave as links.
   */
  continueWhenClean: pageContinueWhenClean,
  collapseRequestSignal,
  onCollapsePromptResolved,
  onLinkNavigate,
  flatSort = 'shoot',
  sortDir = 'asc',
  onHeaderSort,
  groupHeaderTop = 0,
  deepExpandGagId,
  onDeepExpandDone,
  /**
   * ── User-draggable column widths (2026-08-09, increment 2) ──────────
   *
   * 🔑 **ALL THREE ARE OPTIONAL, AND OMITTING THEM RENDERS EXACTLY THE TABLE
   * THAT SHIPPED BEFORE THIS FEATURE** — the measured defaults, no handles.
   * That is the same device `ui/SortableHeader`'s optional `onSort` uses, and
   * it is what keeps every existing test of this component honest: a bare
   * `<GagGroupTable>` is not a degraded mode, it is the default rendering.
   *
   * 🚨 **THE WIDTHS COME FROM THE PAGE, NOT FROM HERE, AND THIS COMPONENT MUST
   * NEVER CALL `useColumnWidths` ITSELF.** The Breakdown renders N `<table>`s
   * inside one horizontal scroll region forced to a common width; a drag has to
   * resize that column in EVERY group at once. One map, owned above, rendered
   * into every group's `<colgroup>` — so the groups cannot diverge because
   * there is no per-group state to diverge with. State held down here would
   * still be shared across groups (this component renders all of them), but the
   * page also needs `resetAll` for its `Actions ▾` menu, and lifting it later
   * through a ref is how one width model quietly becomes two.
   */
  columnWidths = null,
  onColumnResize = null,
  onColumnResetWidth = null,
  /**
   * `Fit columns to screen` (2026-09-27): receives the width one group's
   * table pane offers (`panelWidth - 2`, the `overflowsX` test below), or
   * `null` before the first measurement. The page owns the menu item and the
   * width hook, and this component owns the ONE region measurement — this
   * reports it up rather than the page taking a second one. Optional.
   */
  onRegionWidthChange = null,
  /** Reports the id of the gag whose row is expanded, or `null` (and `null` on
   *  unmount), so the page can remember it in the history entry
   *  (`hooks/useViewInHistory#useOpenRowInHistory`, 2026-10-13). Optional. */
  onExpandedGagChange = null,
  /** 📱 The phone table's width map (`GAG_PHONE_COL_WIDTHS_KEY`), owned by
   *  the page like `columnWidths`. Optional — omitted, the declared widths. */
  phoneColumnWidths = null,
}) {
  // The LIVE grouping — what the current gags/groupBy/sort say the table
  // should look like right now. `groups` below may deliberately lag it while a
  // row is being edited; see the freeze under the inline-edit state.
  const liveGroups = useMemo(
    () => buildGroups(gags, groupBy, flatSort, sortDir),
    [gags, groupBy, flatSort, sortDir]
  );
  const flatMode = groupBy === 'all';
  const [assigningId, setAssigningId] = useState(null);
  const [assignTo, setAssignTo] = useState('');

  // ── Which twin hosts the expanded panel ─────────────────────
  // The desktop table is CSS-hidden below md (hidden md:block) and the
  // phone table mounts only below md — and GagDetailPanel fetches
  // four endpoints on mount and owns a whole task board, so exactly ONE
  // instance may mount per expanded gag. Mirror the same 768px breakpoint in
  // JS (house pattern: DepartmentOrders/DepartmentHires) and host the panel
  // in whichever table is actually on screen.
  // Trade-off: dragging a desktop window ACROSS 768px with an expanded panel
  // re-hosts (remounts) it, so unsaved edits in that panel are discarded —
  // the same silent-discard class as a group-by change, and far rarer.
  const [isNarrow, setIsNarrow] = useState(() =>
    typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(NARROW_QUERY).matches
  );
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(NARROW_QUERY);
    const handler = (e) => setIsNarrow(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // ── Full-width panel fix (Round 2, all variants) ────────────
  // The expanded panel lives inside a horizontally scrollable scrollport
  // (the group's own until 2026-08-08; the ONE page-level region since),
  // whose content (the table) is wider than the viewport. `sticky left-0`
  // pins it, but its width was capped at min(72rem, 100vw-2rem) — on wide
  // screens the chart lost most of the page. Measure the component's own
  // root instead (= the real visible content width: viewport minus page
  // padding minus sidebar) and size the panel to it exactly.
  //
  // The measuring effect itself moved to `hooks/useMeasuredWidth.js` on
  // 2026-08-08, when `components/OrderTableView.js` needed the identical
  // machinery for the identical reason (its expanded panel had the same
  // 72rem cap, and its table became full width). The `Math.round` loop guard
  // and the ResizeObserver-vs-window-resize reasoning live there now, beside
  // the code they explain — this is the same behaviour, not a rewrite.
  /**
   * 🔀 **`rootRef` MOVED ONTO THE SCROLL REGION on 2026-08-08, and it had to.**
   * It used to sit on the inner `space-y-3` list, which was the visible content
   * width because each group owned its own horizontal scrollport. Now there is
   * ONE page-level region and the list inside it is `md:w-max` — as wide as the
   * widest table, i.e. the SCROLL width, not the visible width. Measuring the
   * list would size the panel to the content and push most of it past the right
   * edge, where a `sticky left-0` element can never be reached.
   *
   * The region's `clientWidth` is the visible content box with its own vertical
   * scrollbar already excluded, which is exactly what the panel must not exceed
   * — the same anchor `OrderTableShell` measures.
   *
   * ⚠️ **The `- 2` at the use site STAYS, and stays justified for the same
   * reason it always was: the group CARD has a 1px border on each side and the
   * panel lives inside it.** `OrderTableShell` subtracts nothing because its
   * panel is not inside a bordered card. The two differ because the elements
   * differ, not because one of them is stale — do not unify them.
   */
  const [rootRef, panelWidth] = useMeasuredWidth();
  useEffect(() => {
    if (typeof onRegionWidthChange !== 'function') return undefined;
    onRegionWidthChange(panelWidth != null && panelWidth > 0 ? panelWidth - 2 : null);
    // Unmounted (Timeline, an empty list) there is no table to fit: say so,
    // rather than leave the page holding the last width it saw.
    return () => onRegionWidthChange(null);
  }, [panelWidth, onRegionWidthChange]);

  /**
   * 🔀 **THE REGION'S MEASURED TOP IS GONE (2026-09-26).** It fed the inner
   * region's `maxHeight` (`useMeasuredDocumentTop` + `resolveStickyRegionTop`,
   * added 2026-08-10 because a cap against `groupHeaderTop` overhung the fold).
   * The region no longer scrolls or caps anything — see the ONE PAGE SCROLL
   * block at this component's return — so both helpers lost their last caller
   * and were DELETED with `stickyRegionMaxHeight` and `STICKY_REGION_AXES_MD`
   * (pre-deletion SHA `3e60c14d`). `groupHeaderTop` is once again exactly the
   * right number, for the job it had before 2026-08-08: where each group's
   * sticky HEAD pins against the page.
   */

  /**
   * ── THE ONE WIDTH MODEL (2026-08-09, increment 2) ────────────
   *
   * 🔑 **ONE map, rendered into every group's `<colgroup>`.** The `groups.map`
   * below closes over these values; nothing per-group is derived from them and
   * nothing per-group may be. Per-group widths look perfectly correct in a
   * department with a single group and break the moment there are two — which
   * is the ordinary case — and the symptom (group cards no longer lining up
   * inside the shared horizontal scroll region) is the exact thing `md:w-max`
   * was introduced, and later replaced by a definite `minWidth`, to fix.
   *
   * 🪤 **`totalWidth` IS LIVE; `GAG_TABLE_MIN_WIDTH` IS THE DEFAULTS' SUM AND
   * IS NO LONGER IT.** Feeding that constant to the list wrapper or the table
   * after a user has resized anything means the declared columns no longer add
   * up to the box they are drawn in: either the cards stop matching the tables,
   * or the table refuses to shrink below a floor nobody chose. It is no longer
   * imported by this file at all, which is the cheapest possible guard.
   *
   * `Number.isFinite` on the lookup so a caller passing a partial map (or a
   * column added since a stored layout was written) falls back to that column's
   * measured default rather than summing `undefined` into `NaN` — which would
   * blank the `minWidth` on every table at once.
   */
  /**
   * 🚨 **THE FROZEN BLOCK IS BOUNDED AGAINST THE MEASURED REGION HERE, AND THIS
   * IS THE ONLY PLACE IT CAN BE.** `expand` and `title` are both frozen and both
   * draggable to `MAX_GAG_COLUMN_WIDTH`, so their sum could reach 1,200px
   * against a 976px scroll region at 1280 — at which point the frozen block
   * covers the whole scrollport and **every other column is unreachable at every
   * scroll offset**, permanently, because the widths are persisted. Measured
   * threshold: `block >= region`. See `boundFrozenColumnWidths`.
   *
   * 🔑 **It has to happen where the REGION is measured, not where the widths are
   * owned.** `useColumnWidths` lives on the page and knows nothing about the
   * scrollport; `panelWidth` here IS the region's `clientWidth` (`rootRef` sits
   * on the region — see the note above it). Deriving the bound at render is also
   * what makes it survive a window resize or a sidebar collapse AFTER the drag,
   * which a drag-time clamp cannot: the stored width outlives the viewport that
   * produced it.
   *
   * 🔑 **ONE map still.** The bound is applied ONCE, here, and everything
   * downstream — `totalWidth`, `frozenLeft`, every group's `<colgroup>`, every
   * resize handle's `aria-valuenow` — reads this same object, so the offsets
   * cannot disagree with the widths they are offsets into. Bounding it at any
   * one of those sites instead would be exactly that disagreement.
   *
   * ⚠️ **`panelWidth` is `null` until the first measurement lands** (an effect,
   * so after the first paint), and `boundFrozenColumnWidths` then returns the
   * stored map unchanged — a guessed region width is the `100vw` trap
   * `useMeasuredWidth` exists to avoid. An over-wide stored layout is therefore
   * bounded on load, one frame late rather than not at all.
   */
  const storedWidths = columnWidths || DEFAULT_GAG_COLUMN_WIDTHS;
  const widths = useMemo(
    () => boundFrozenColumnWidths(storedWidths, panelWidth),
    [storedWidths, panelWidth]
  );
  const totalWidth = useMemo(
    () => GAG_TABLE_COLUMNS.reduce(
      (sum, c) => sum + (Number.isFinite(widths[c.key]) ? widths[c.key] : c.width),
      0
    ),
    [widths]
  );
  /**
   * ── FROZEN COLUMNS (2026-08-09) ──────────────────────────────
   *
   * The `left` each frozen column pins at, recomputed whenever the width map
   * changes. **Derived from the LIVE widths, never the defaults**: the frozen
   * columns stay draggable, so Title's offset is the chevron column's CURRENT
   * width — read the defaults and dragging the chevron opens a transparent gap
   * between the two frozen columns, or slides Title on top of one.
   *
   * 🔑 Shared by every group table for the same reason `widths` is: the offsets
   * are a function of one width map, so N tables cannot disagree about where a
   * frozen column starts. See `constants/gagTableColumns.js` for why exactly
   * these two columns are frozen and why a third is a product decision.
   */
  const frozenLeft = useMemo(() => frozenGagColumnOffsets(widths), [widths]);

  /**
   * Handles render only when the page supplied BOTH callbacks — the same
   * "omit the callback and the control is not offered" shape `SortableHeader`
   * uses for `onSort`, so a bare `<GagGroupTable>` renders the table exactly as
   * it did before this feature.
   *
   * 📱 **Never below `md`.** The table is `hidden md:block` down there (the
   * phone table takes over, resize off), so a handle would be a
   * tab stop on an invisible control. It also matches where the persisted
   * widths are applied: the inline `minWidth` styles are gated on `isNarrow`
   * for the same reason (an inline style cannot carry a breakpoint).
   */
  const resizable = typeof onColumnResize === 'function'
    && typeof onColumnResetWidth === 'function'
    && !isNarrow;

  /**
   * The sticky group HEAD's measured height (title + column headers, one
   * block). 🔀 Until 2026-09-26 this measured the title alone, because the
   * column headers pinned BELOW it at that offset inside the inner region; the
   * two now pin as ONE sticky block, so no offset between them exists to get
   * wrong (and the 1px device-pixel seam that offset had to cover is gone with
   * it — nothing scrolls between them).
   *
   * What it is still for: `scroll-margin-top` on the rows, so a `?gag=` deep
   * link's `scrollIntoView({ block: 'start' })` lands the row BELOW the pinned
   * toolbar + head rather than under them. Measured, never the arithmetic — a
   * long sub-department label wraps. Attached to the FIRST group's head only:
   * every head is the same element, so one observer serves all of them.
   */
  const [groupHeadRef, groupHeadHeight] = useMeasuredHeight();
  const rowScrollMarginTop = groupHeaderTop + (groupHeadHeight || 0);

  /**
   * ── ONE PAGE SCROLL: THE LINKED PANES (2026-09-26) ─────────────
   *
   * Every group's header pane and rows pane, plus the one sticky-bottom bar,
   * scroll sideways TOGETHER (the 2026-08-08 "all groups scroll sideways
   * together" property, kept). The set changes as groups expand and collapse,
   * so it is found by a DOM query at effect time — the hook's FUNCTION form —
   * rather than through refs built during render, which would miss the panes
   * that very render mounts.
   */
  useLinkedScrollLeft(() => (rootRef.current
    ? [...rootRef.current.querySelectorAll('[data-page-scroll-pane]')]
    : []));
  // 🖱️ Click-and-drag panning (2026-09-27): a drag pans the GROUP pane under
  // the pointer sideways (the link above moves every other group) and the PAGE
  // vertically. An open row and its `GagDetailPanel` are no-pan — see
  // `data-pan-skip` on both — and the kebab menu is portalled out of this box.
  useDragScroll(rootRef, { axis: 'both', verticalPansWindow: true });
  /**
   * The bar shows only while the table is WIDER than a pane. The panes sit
   * inside the group card's 1px border each side, so a pane is `panelWidth - 2`
   * wide (the same `- 2` the expanded panel and the resize handle use), and
   * the bar — which spans the root, border to border — carries a spacer 2px
   * wider than the tables so its scroll RANGE equals theirs. `null` or `0` =
   * not measured (a `display: none` ancestor, or jsdom): no bar — the same
   * reading `OrderTableShell` gives a measured 0.
   */
  const overflowsX = panelWidth != null && panelWidth > 0 && totalWidth > panelWidth - 2;

  // ── Expand-to-edit detail panel (Task 3.2) ──────────────────
  // Rows are keyed `${group.key}::${gag.id}` — under scene grouping the same
  // gag appears in several groups, and only the CLICKED row expands.
  // openingKey/closingKey drive the documented 200ms opacity fade.
  const [expandedKey, setExpandedKey] = useState(null);
  // The key is `${group.key}::${gag.id}`: the id is what follows the LAST `::`.
  useEffect(() => {
    if (typeof onExpandedGagChange !== 'function') return undefined;
    const id = expandedKey ? Number(String(expandedKey).split('::').pop()) : null;
    onExpandedGagChange(Number.isInteger(id) ? id : null);
    return () => onExpandedGagChange(null);
  }, [expandedKey, onExpandedGagChange]);
  const [openingKey, setOpeningKey] = useState(null);
  const [closingKey, setClosingKey] = useState(null);
  // Dirty state is keyed by row, not a plain boolean: a closing panel's
  // unmount cleanup reports (rowKey, false) and can only clear ITS OWN flag —
  // never a freshly-expanded row's dirtiness.
  const [dirtyKey, setDirtyKey] = useState(null);
  const [showDirtyWarning, setShowDirtyWarning] = useState(false);

  // ══════════════════════════════════════════════════════════════════════
  // INLINE ROW EDITING (2026-08-09) — PROTOTYPE
  // ══════════════════════════════════════════════════════════════════════
  // Product owner: *"On breakdown. when we expand a table can we make that
  // table row editable? I'm thinking of editing directly in the teable row for
  // table row items and the Gag Details section will be for additional
  // detials."*
  //
  // Expanding a row turns ITS OWN cells into controls; `GagDetailPanel` stays
  // mounted below for everything the row does not show.
  //
  // 📱 **THE PANEL CAN NEVER SHRINK TO "ADDITIONAL DETAILS ONLY", AND THIS IS
  // NOT A PHASE-1 SHORTCUT.** The table is `hidden md:block` and below `md`
  // the same gags render as CARDS with no row cells to edit. So inline editing
  // is a DESKTOP SHORTCUT over a panel that must stay complete — on a phone
  // the panel is the only editor there is. Nobody should later "finish the
  // job" by gutting `GagDetailPanel`; doing so would remove the ability to
  // edit a gag on mobile entirely.
  //
  // 🔑 SINGLE-EXPAND, inherited free: this table already allows exactly one
  // expanded row (`expandedKey` is a single key, not a Set). ⚠️ The reason is
  // NOT the id-collision that forces the ORDER table into an accordion —
  // `GagDetailPanel` was counted rather than assumed and has **zero**
  // `htmlFor` / `aria-describedby` / `aria-controls` / `aria-labelledby` and
  // two `id=` attributes, both already scoped by `${gag.id}`. Multi-expand is
  // reachable here; it is simply not what the prototype needs.
  //
  // Draft lives beside `expandedKey` rather than inside the row: the row
  // component re-renders on every keystroke and must not own the value.
  const [editDraft, setEditDraft] = useState(null);   // {colKey: string} | null
  const [editKey, setEditKey] = useState(null);       // rowKey the draft belongs to
  const [savingRow, setSavingRow] = useState(false);
  const [rowSaveError, setRowSaveError] = useState(null);
  // The rowKey whose collapse is waiting on the three-way prompt, or null.
  // Keyed by row rather than a boolean for the same reason `dirtyKey` is: a
  // stale `true` outliving its row would be a dialog about nothing.
  const [collapsePromptKey, setCollapsePromptKey] = useState(null);

  // The gag the draft was seeded from — dirtiness is measured against THIS,
  // not against whatever the latest fetch produced, so a background refresh
  // cannot silently mark a user's untouched row dirty (or clean).
  const editBaseRef = useRef(null);

  const rowDirty = !!(editDraft && editBaseRef.current
    && isGagDraftDirty(editBaseRef.current, editDraft));

  // 🔑 ONE DIRTY SIGNAL. The page's guards (collapse, Table↔Timeline switch,
  // saved-view apply, group-header collapse, deep-expand) all read this, so an
  // unsaved ROW edit is protected by exactly the machinery that already
  // protects an unsaved PANEL edit — no second guard to keep in step.
  const panelDirty = dirtyKey != null || rowDirty;

  // ── Ask-then-continue (2026-09-29) — see the prop docblock. Always called
  // (hooks cannot be conditional); used only when the page supplies none.
  const localContinuation = useDirtyContinuation({ dirty: panelDirty });
  const pageOwnsContinuation = typeof pageContinueWhenClean === 'function';
  const continueWhenClean = pageOwnsContinuation ? pageContinueWhenClean : localContinuation.continueWhenClean;
  const promptSignal = pageOwnsContinuation ? collapseRequestSignal : localContinuation.promptSignal;
  const reportPromptOutcome = (outcome) => {
    if (pageOwnsContinuation) onCollapsePromptResolved?.(outcome);
    else localContinuation.onPromptResolved(outcome);
  };
  // `GagDetailPanel` publishes its OWN save here (`submitRef`, the OrderForm
  // device) so `Save and close` saves a dirty panel form through the one
  // save path its Save button uses. Single-expand → one panel → one ref.
  const panelSubmitRef = useRef(null);
  // …and its discard (`discardRef`): with a shared draft it drops ONLY an
  // inline task edit — the row bar's Discard pairs it with `discardDraft`.
  const panelDiscardRef = useRef(null);
  // The row bar's "Save all" in flight (gag PUT, then the task's save).
  const [savingAll, setSavingAll] = useState(false);
  // The rowKey whose bar Discard is asking "lose the task edit too?", or null.
  const [discardConfirmKey, setDiscardConfirmKey] = useState(null);
  // Held actions re-enter the LATEST handler (the hook's docblock, point 2).
  const toggleRowRef = useRef(null);
  const expandRowRef = useRef(null);
  const onToggleGroupRef = useRef(onToggleGroup);
  useEffect(() => { onToggleGroupRef.current = onToggleGroup; });

  // ══════════════════════════════════════════════════════════════════════
  // 🔴 THE SORT-AND-GROUP FREEZE — the defect inline editing creates
  // ══════════════════════════════════════════════════════════════════════
  // Editing the field you are SORTED BY relocates the row mid-edit: change a
  // gag's status while sorted by Status and the row you are typing into jumps
  // somewhere else in the list, under the cursor. One shared sort state drives
  // every group table, so it jumps in all of them at once.
  //
  // 🔑 **AND SORT IS ONLY HALF OF IT — GROUPING IS WORSE.** Editing
  // `sub_department` while grouped by sub-department moves the row into a
  // DIFFERENT GROUP CARD, so it does not merely move, it leaves the table you
  // were looking at. Freezing the sort alone would have left that live.
  //
  // So the whole group structure is frozen while any row is dirty — the
  // simplest answer that is actually correct, which is what a prototype wants.
  // A snapshot is taken on the clean→dirty transition and released on save or
  // discard, at which point the row settles into its real position.
  //
  // ⚠️ **THE COST, STATED SO IT IS NOT DISCOVERED AS A BUG:** while a row is
  // dirty the table does not re-sort, does not re-group, and does not take in
  // gags added by a background refresh. That is a deliberate trade — a list
  // that reorders under an open editor is worse — but it means the frozen
  // snapshot holds STALE gag objects for every other row. Harmless here
  // because nothing else on the page is editable at the same time (single
  // expand), and it resolves the instant the edit ends.
  //
  // 🪤 Mutating a ref during render is safe ONLY because both branches are
  // idempotent: seeding when null yields the same object under React's
  // double-render, and clearing when clean is a no-op the second time.
  const frozenGroupsRef = useRef(null);
  if (panelDirty) {
    if (!frozenGroupsRef.current) frozenGroupsRef.current = liveGroups;
  } else if (frozenGroupsRef.current) {
    frozenGroupsRef.current = null;
  }
  const groups = frozenGroupsRef.current || liveGroups;

  const handlePanelDirty = (rowKey, dirty) => {
    setDirtyKey((prev) => (dirty ? rowKey : prev === rowKey ? null : prev));
  };

  // Auto-clear the blocked-collapse pulse (BudgetCostCodes pattern).
  useEffect(() => {
    if (!showDirtyWarning) return undefined;
    const timer = setTimeout(() => setShowDirtyWarning(false), 2000);
    return () => clearTimeout(timer);
  }, [showDirtyWarning]);

  // ── Dirty-state lift + page-level warning poke (Task 4.2 fix) ──
  // Report panelDirty transitions up (ref-stabilised like GagDetailPanel's
  // onDirtyChange), and clear the page's flag on unmount so a discarded
  // panel (dept switch, deleted view) can't leave a stale block behind.
  const onDirtyStateChangeRef = useRef(onDirtyStateChange);
  useEffect(() => { onDirtyStateChangeRef.current = onDirtyStateChange; });
  useEffect(() => { onDirtyStateChangeRef.current?.(panelDirty); }, [panelDirty]);
  useEffect(() => () => onDirtyStateChangeRef.current?.(false), []);

  // Each dirtyWarnSignal bump = the page blocked a view switch on our dirty
  // panel — fire the same pulse a blocked collapse fires. The ref seeds with
  // the MOUNT value so a remount with a non-zero counter doesn't pulse.
  const prevWarnSignal = useRef(dirtyWarnSignal);
  useEffect(() => {
    if (dirtyWarnSignal !== prevWarnSignal.current) {
      prevWarnSignal.current = dirtyWarnSignal;
      setShowDirtyWarning(true);
    }
  }, [dirtyWarnSignal]);

  // ── Inline row edit: draft lifecycle ────────────────────────
  // Seeded when a row expands, dropped when it collapses. Deliberately NOT
  // keyed by gag id: under scene grouping the same gag appears in several
  // groups and only the clicked ROW is editable, so the row key is the
  // identity — the same choice `expandedKey` and `dirtyKey` already make.
  const openRowEditor = (rowKey, gag) => {
    editBaseRef.current = gag;
    setEditDraft(gagDraftFrom(gag));
    setEditKey(rowKey);
    setRowSaveError(null);
  };
  const closeRowEditor = () => {
    editBaseRef.current = null;
    setEditDraft(null);
    setEditKey(null);
    setRowSaveError(null);
    setSavingRow(false);
    // 🪤 The collapse prompt goes with the draft it is about. Without this a
    // save that succeeds WHILE the prompt is open (chevron clicked mid-save)
    // would leave a dialog asking about an edit that no longer exists, with
    // nothing dirty for any of its three buttons to act on.
    setCollapsePromptKey(null);
    setDiscardConfirmKey(null);
  };
  const setEditField = (colKey, value) => {
    setRowSaveError(null);
    setEditDraft((d) => (d ? { ...d, [colKey]: value } : d));
  };

  /**
   * 🐛 REBASE THE OPEN ROW'S DRAFT ON A FRESH SERVER GAG (owner-reported
   * 2026-09-29: *"if I click 'Create team for this gag' this makes me the gag
   * owner BUT 'Owner' doesnt update"*). The draft was seeded ONCE at expand, so
   * any server-side change to the open gag — create-team's owner claim and
   * sub-dept autofill, a panel save, a kebab take/assign/release, a refetch —
   * left the row's controls showing the pre-change values until a re-expand.
   *
   * Every field the user has NOT touched (draft === old base) takes the fresh
   * value; a field they changed keeps their edit; the base becomes the fresh
   * gag, so the diff-only PUT compares against current server state and never
   * re-sends a stale value for a field the user did not edit. A clean draft is
   * the special case where every field takes the fresh value.
   *
   * 🔑 The fresh gag is looked up in the LIVE `gags` prop by id, NOT in
   * `groups`: while anything is dirty the sort-and-group FREEZE serves the
   * snapshot, which holds the STALE gag object — reading `groups` here would
   * never see the change. (The same freeze means the row's READ-ONLY cells
   * keep showing snapshot values while dirty; they settle on save/discard.)
   */
  useEffect(() => {
    const base = editBaseRef.current;
    if (!editKey || !base) return;
    const fresh = (gags || []).find((g) => g.id === base.id);
    if (!fresh || fresh === base) return;
    const baseDraft = gagDraftFrom(base);
    const freshDraft = gagDraftFrom(fresh);
    editBaseRef.current = fresh;
    // Always a new object: the BASE moved, so `rowDirty` (computed at render
    // against `editBaseRef`) must be re-evaluated even if no field changed.
    // Since 2026-09-29 the draft is the ONE draft the panel edits too, so this
    // rebases the panel-only fields as well.
    setEditDraft((d) => (d ? rebaseGagDraft(d, baseDraft, freshDraft) : d));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gags, editKey]);

  // Bumped after every successful save of the draft, so the panel re-reads its
  // detail (audit, scenes, names) whichever Save was pressed.
  const [draftSavedSignal, setDraftSavedSignal] = useState(0);
  const discardDraft = () => {
    setEditDraft(gagDraftFrom(editBaseRef.current));
    setRowSaveError(null);
  };
  // The panel writes the shared draft through its own form shape.
  const setDraftFromPanel = (updater) => {
    setRowSaveError(null);
    setEditDraft((d) => {
      if (!d) return d;
      const nextForm = typeof updater === 'function' ? updater(panelFormFromDraft(d)) : updater;
      return { ...d, ...draftPatchFromPanelForm(nextForm) };
    });
  };

  /**
   * Save the row's diff.
   *
   * 🚨 **REUSES `PUT /api/gags/:gagId` AND MUST CONTINUE TO.** That handler
   * already owns the whole write contract: the `canEditGag` gate on field
   * edits, the separate owner take/assign/release ladder, `normalizeGagField`
   * validation, the `updated` audit row plus the conditional `status_changed`
   * one, the shoot-date notification and the forward gag-title→team-name sync.
   * A second writer of `gags` would have to re-implement every one of those,
   * and "temporary" second writers in this repo have never stayed temporary.
   *
   * ⚠️ A save that includes an owner change is therefore NOT a single-audit-row
   * operation — it can emit `owner_changed` or `released` as well. That is the
   * endpoint behaving correctly, not something to smooth over.
   *
   * 🚨 **`onSaved` RUNS ON THE SUCCESS PATH ONLY, AND THAT IS STRUCTURAL.** The
   * collapse prompt's `Save and close` hangs the collapse off it, so a
   * validation 400, a 403 and a dropped connection ALL leave the row open
   * showing the server's own message — with no `catch` anywhere that has to
   * remember not to collapse. Copied from the order pages, where CLAUDE.md
   * records the same construction and the same reason.
   */
  // 🔀 2026-09-29: this saves the ONE draft the row and the panel share, from
  // either place (row bar, Enter, the panel's Save, `Save and close`): every
  // pending change in ONE diff-only PUT, never a second request. On success
  // the row STAYS OPEN and both views re-seed from the saved gag (coordinator
  // ruling — it used to close the row editor, which would now orphan the
  // panel's); only an explicit collapse closes it.
  const saveRowEdit = async (gag, { onSaved } = {}) => {
    // Resolves true on success (incl. a no-op), false on any failure — the
    // `Save and close` chain reads it (2026-09-29) so a failed row save stops
    // the chain before the panel is saved or any held action runs.
    const base = editBaseRef.current;
    if (!base || !editDraft) return true;
    const submitted = editDraft;
    const body = buildGagDraftPatch(base, submitted);
    // A no-op save is a success: nothing to write, so the caller's onSaved
    // (i.e. the collapse) must still run.
    if (Object.keys(body).length === 0) { onSaved?.(); return true; }
    // Refused BEFORE the request, whichever Save was pressed: a cleared title,
    // or a malformed cost the server would read as "clear" (it would ERASE it).
    const invalid = gagDraftSaveError(submitted);
    if (invalid) { setRowSaveError(invalid); return false; }
    setSavingRow(true);
    setRowSaveError(null);
    try {
      const res = await apiPut(`/api/gags/${gag.id}`, body);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        // Stay open, keep the draft, show the server's own message. A failed
        // save must never look like a successful one, and must never discard
        // the user's typing.
        setRowSaveError(data.error || `Failed to save (HTTP ${res.status})`);
        setSavingRow(false);
        return false;
      }
      // The saved state is the new base: the PUT's echo when it is this gag,
      // else the base with what was sent applied. Anything typed WHILE the
      // request was in flight survives (it differs from what was submitted).
      const echoOk = data && typeof data === 'object' && Number(data.id) === Number(base.id);
      const newBase = echoOk ? { ...base, ...data } : gagWithDraftApplied(base, submitted);
      editBaseRef.current = newBase;
      setEditDraft((d) => (d ? rebaseGagDraft(d, submitted, gagDraftFrom(newBase)) : d));
      setSavingRow(false);
      setDraftSavedSignal((n) => n + 1);
      onSaved?.();
      onGagUpdated?.();
      return true;
    } catch {
      setRowSaveError('Failed to save — check your connection.');
      setSavingRow(false);
      return false;
    }
  };

  const collapseRow = () => {
    if (!expandedKey) return;
    // Capture the key: if another row expands inside the 250ms fade window,
    // this timeout must not clear THAT row's fresh expansion. panelDirty was
    // already false (toggleRow's guard), and the closing panel's unmount
    // cleanup resets it anyway — so don't touch it here either.
    const key = expandedKey;
    setClosingKey(key);
    setTimeout(() => {
      setExpandedKey((cur) => (cur === key ? null : cur));
      setClosingKey((cur) => (cur === key ? null : cur));
      // Keep the collapsed gag's summary row in view. Removing the (often
      // viewport-sized) panel shrinks the document; on short lists the
      // browser then CLAMPS scrollTop — with few gags that lands at/near 0
      // ("the page scrolled to the top", Pete's report). Only intervene
      // when the row actually left the viewport — a no-clamp collapse must
      // not yank the scroll. Runs just after React commits the removal.
      setTimeout(() => {
        const row = findRowEl(key);
        if (!row) return;
        const rect = row.getBoundingClientRect();
        const vh = window.innerHeight || document.documentElement.clientHeight;
        if (rect.top < 0 || rect.bottom > vh) {
          row.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      }, 60);
    }, 250);
  };

  // `gag` is required for the inline row editor's draft. It comes from the row
  // model, which already has it — the parameter was added rather than looking
  // the gag up again, so the row and its draft can never be seeded from two
  // different objects.
  // ══════════════════════════════════════════════════════════════════════
  // 🔀 THE DIRTY-COLLAPSE PROMPT (2026-08-09) — ONLY THIS GESTURE ASKS
  // ══════════════════════════════════════════════════════════════════════
  // Collapsing a row that holds unsaved ROW edits raises the same three-way
  // dialog the order pages raise — `Keep editing` · `Discard changes` ·
  // `Save and close` — instead of being refused with the amber pulse.
  //
  // 🚨 **CLAUDE.md WARNS IN TERMS AGAINST GENERALISING THAT PROMPT** (*"a rule
  // generalised from one caller's change is how two unrelated screens get
  // 'fixed'"*), and the prototype honoured that warning by not adopting it at
  // all. The product owner has now authorised it **for this gesture**, on
  // CONSISTENCY grounds rather than discoverability: the order pages already
  // prompt on the same gesture, and one gesture meaning two different things in
  // one app is worse than either behaviour alone.
  //
  // 🔀 **SUPERSEDED 2026-09-29 — EVERY GESTURE NOW ASKS, AND PANEL DIRT IS
  // COVERED.** Owner: *"making an edit and clicking on a different gag, page
  // link etc should give us the same style pop up that we've added in for hires
  // and orders"*. Collapse, expanding another row, a group header, the
  // Table/Timeline/Labour switch, the `?gag=` deep link and an in-panel app
  // link all go through `continueWhenClean` (`hooks/useDirtyContinuation.js`):
  // clean → the gesture runs; dirty → this prompt, then the gesture CONTINUES
  // on `Save and close` (ok) or `Discard changes`, and is DROPPED on
  // `Keep editing` or a failed save. The held action runs in the hook's effect
  // once clean, never in the click handler.
  // ✅ The panel half the old text called "owed": `GagDetailPanel` publishes
  // its own save through `submitRef`, so `Save and close` saves the ROW draft
  // (if dirty) then the PANEL form (if dirty) — each through its own one save
  // path — and any failure stops the chain: no collapse, no continued action.
  // 🔀 A dirty INLINE TASK edit inside the panel (TeamTaskBoard) is saved by
  // the panel's `submitRef` too since 2026-10-07 (TaskEditor's own `save`);
  // a FAILED task save reports failed and pulses it; Discard drops it.
  const toggleRow = (rowKey, gag) => {
    if (expandedKey === rowKey) {
      // The plain collapse prompt: holds nothing, the outcome does the work.
      if (panelDirty) { continueWhenClean(); return; }
      collapseRow();
      closeRowEditor();
      return;
    }
    if (expandedKey && panelDirty) {
      continueWhenClean(() => toggleRowRef.current(rowKey, gag));
      return;
    }
    expandRow(rowKey, gag);
  };
  toggleRowRef.current = toggleRow;

  function expandRow(rowKey, gag) {
    setOpeningKey(rowKey);
    setExpandedKey(rowKey);
    // 🔑 Only rows the SERVER says are editable get a draft. `can_edit` is
    // computed per gag by `GET /api/gags` and is never re-derived here — a row
    // without it expands to the read-only cells plus the detail panel, exactly
    // as it did before inline editing existed.
    if (gag && gag.can_edit === true && !deletedView) openRowEditor(rowKey, gag);
    else closeRowEditor();
    setTimeout(() => setOpeningKey(null), 50); // trigger fade-in on next frame
  }
  expandRowRef.current = expandRow;

  // Each bump of the (page's or local) prompt signal opens the ONE prompt on
  // the expanded row. Seeded from the MOUNT value, so a remount with a non-zero
  // counter prompts nothing (the order editor's rule).
  const prevPromptSignal = useRef(promptSignal);
  useEffect(() => {
    if (promptSignal === prevPromptSignal.current) return;
    prevPromptSignal.current = promptSignal;
    if (expandedKey) setCollapsePromptKey(expandedKey);
    else reportPromptOutcome('kept'); // nothing open to ask about: drop it
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promptSignal]);

  // Each collapseAllSignal bump closes the open row (the page already asked).
  const prevCollapseAllSignal = useRef(collapseAllSignal);
  useEffect(() => {
    if (collapseAllSignal === prevCollapseAllSignal.current) return;
    prevCollapseAllSignal.current = collapseAllSignal;
    if (!expandedKey) return;
    collapseRow();
    closeRowEditor();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapseAllSignal]);

  // ── The prompt's three outcomes ─────────────────────────────
  // Named after what each DOES, matching the button labels, so the wiring and
  // the copy cannot drift apart. Each REPORTS its outcome, which is what
  // releases (saved / discarded) or drops (kept / failed) a held gesture.
  const keepEditing = () => {
    setCollapsePromptKey(null);
    reportPromptOutcome('kept');
  };

  const discardAndClose = () => {
    setCollapsePromptKey(null);
    // Drop the draft FIRST: `collapseRow` documents that `panelDirty` is
    // already false by the time it runs, and after this it is. The panel's
    // flag goes too — its form (and any inline task edit) unmounts with the
    // collapse, which is the discard.
    closeRowEditor();
    setDirtyKey(null);
    collapseRow();
    reportPromptOutcome('discarded');
  };

  /**
   * 🚨 THE ONE "save everything under this gag" — `Save and close` AND the row
   * bar's Save (2026-10-18, owner: *"make them save the work as well"*). The
   * row's own `saveRowEdit` (the shared draft) FIRST; if that fails, stop — the
   * task is never attempted. Then the panel's `submitRef` (an inline task edit
   * via TaskEditor's own save). True only when every dirty part saved.
   * 🔑 A task refusal after a SAVED gag says exactly that, with the task's own
   * message — never a generic failure, never a state that looks like nothing
   * saved. `rowSaveError` is the bar's one message line.
   */
  const saveGagAndTask = async () => {
    const rowWasDirty = rowDirty;
    const panelWasDirty = dirtyKey != null;
    const base = editBaseRef.current;
    if (rowWasDirty && base) {
      if (!(await saveRowEdit(base))) return false;
    }
    if (panelWasDirty) {
      const submit = panelSubmitRef.current;
      let taskError = null;
      const ok = submit ? await submit({ onTaskError: (m) => { taskError = m; } }) : false;
      if (!ok) {
        setShowDirtyWarning(true);
        if (taskError) {
          setRowSaveError(rowWasDirty
            ? `Gag saved; the task wasn't — ${taskError}`
            : `The task wasn't saved — ${taskError}`);
        }
        return false;
      }
      // Saved = clean. Cleared here rather than waiting for the panel's own
      // report, which arrives a render later.
      setDirtyKey(null);
    }
    return true;
  };

  const saveAndClose = async () => {
    setCollapsePromptKey(null);
    if (!(await saveGagAndTask())) { reportPromptOutcome('failed'); return; }
    closeRowEditor();
    collapseRow();
    reportPromptOutcome('saved');
  };

  // The row bar's Save ("Save gag" / "Save all"): the same chain, row stays open.
  const saveFromBar = async () => {
    if (savingRow || savingAll) return;
    setSavingAll(true);
    try { await saveGagAndTask(); } finally { setSavingAll(false); }
  };

  // The row bar's Discard. A task edit would be thrown away too → ASK first.
  const discardEverything = () => {
    setDiscardConfirmKey(null);
    discardDraft();
    panelDiscardRef.current?.();
  };
  const requestBarDiscard = (rowKey) => {
    if (dirtyKey === rowKey) setDiscardConfirmKey(rowKey);
    else discardDraft();
  };

  /**
   * ⌨️ The row editor's keyboard model.
   *
   * **Enter saves. Escape raises the same prompt a chevron click raises** (or
   * just collapses, when the row is clean). Tab needs no code at all — the
   * cells render in `GAG_TABLE_COLUMNS` order and the Save/Discard bar renders
   * immediately after the row, so DOM order already IS visual order, and a
   * focus trap would make the detail panel below unreachable by keyboard.
   *
   * 🪤 **THE THREE THINGS ENTER MUST NOT DO**, each a real control in this row:
   *   - fire inside a `<textarea>` (none today; the guard is what stops the
   *     first one added being a defect),
   *   - steal Enter from a focused `<button>` — Save, Discard, the kebab and
   *     the scene picker's trigger all activate on Enter and own it,
   *   - fire while the scene picker is OPEN, where Enter ticks an option.
   *
   * 🔑 **"Is a dropdown open?" is answered by READING `aria-expanded` off the
   * DOM** — the attribute `ui/Dropdown` already sets and the accessibility tree
   * already reads, so the two can never disagree.
   *
   * 🪤 **It is deliberately NOT done with `stopPropagation` inside the picker.**
   * `ui/Dropdown` closes from a **`document`-level** keydown listener while
   * React attaches its handlers to the root container — which is a DESCENDANT
   * of `document`, so React's handler runs first and a synthetic
   * `stopPropagation` would stop the native event ever reaching `document`.
   * The picker would then never close: Escape would be swallowed by the very
   * code meant to let the picker have it.
   */
  const rowEditKeyDown = (e, rowKey, gag) => {
    if (editKey !== rowKey || editDraft == null) return;
    // An open disclosure owns both keys. Scope the lookup to the element the
    // handler is on so another row's picker can never answer for this one.
    //
    // 🪤 **`[aria-haspopup]` IS LOAD-BEARING, NOT BELT-AND-BRACES.** A bare
    // `[aria-expanded="true"]` matches the row's own EXPAND CHEVRON (`:293`),
    // which is a disclosure too and is `true` on exactly the rows this handler
    // runs for — so the guard would fire on every keypress and Enter and Escape
    // would silently do nothing at all. Measured, not reasoned: that was the
    // first version, and three of §7's cases were red because of it. The same
    // confusion is already recorded at the sticky-header selector below, which
    // had to exclude `button[aria-expanded]` for the same reason.
    //
    // `aria-haspopup` narrows it to a real popover trigger — `ui/Dropdown` sets
    // both, and so does the row's kebab (`:582`), which is correct: an open
    // kebab menu should own these keys as well.
    //
    // 🔀 2026-10-19 (owner, "ONE ENTER SAVES"): the row ASKS THE CONTROL rather
    // than reading its attribute alone. When the key came FROM an open
    // `role="combobox"` (`ui/SuggestInput`) and that control did NOT claim it
    // (no `preventDefault` — Enter with nothing highlighted, `enterPassesThrough`),
    // the list is closing in this same press and the row's Enter runs. A claimed
    // key (a pick, an Escape) still yields. Every other disclosure (Dropdown
    // triggers, the kebab) is untouched. Never `stopPropagation` — see above.
    // Why not drop `aria-expanded` early instead: the attribute is React state,
    // committed AFTER this handler runs, and writing the DOM by hand would
    // lie to the accessibility tree for one frame and fight React's commit.
    // 🪤 UNPINNED BELT (review 2026-10-19): `openEl === e.target` and the
    // `role === 'combobox'` test survive every mutation today — the button
    // early-return above, the Scenes input's own claim and one combobox per row
    // make them unreachable; `!e.defaultPrevented` is the clause that bites.
    // Kept as defence against a future open non-button disclosure that leaves
    // Enter unclaimed. Pin them with such a fixture before relying on them.
    const openEl = e.currentTarget.querySelector('[aria-haspopup][aria-expanded="true"]');
    const comboboxPassedKey = !!openEl && openEl === e.target
      && openEl.getAttribute('role') === 'combobox' && !e.defaultPrevented;
    const dropdownOpen = !!openEl && !comboboxPassedKey;

    if (e.key === 'Enter') {
      const t = e.target;
      if (t.tagName === 'TEXTAREA') return;
      if (t.closest && t.closest('button')) return;
      if (dropdownOpen) return;
      e.preventDefault();
      // The bar's own Save: the gag, then an open task edit (2026-10-18).
      saveFromBar();
      return;
    }
    if (e.key === 'Escape') {
      // Let the picker have it — `ui/Dropdown` closes and returns focus to its
      // trigger. Raising the save/discard dialog here instead would answer a
      // "dismiss this menu" gesture with a "you have unsaved work" question.
      if (dropdownOpen) return;
      e.preventDefault();
      if (!panelDirty) { collapseRow(); closeRowEditor(); return; }
      continueWhenClean(); // the plain collapse prompt, as the chevron raises
    }
  };

  // If the expanded row disappears (group-by/filter change removed it, the
  // gag left the list), drop the expansion + dirty guard so a hidden panel
  // can't block future collapses. Silent discard on group-by/filter changes
  // is documented-acceptable; group-HEADER collapse of the dirty row's group
  // is guarded at the click site instead (it never reaches this effect).
  useEffect(() => {
    if (!expandedKey) return;
    // Flat mode's single pseudo-group is ALWAYS open (no header, nothing
    // collapsible) — expandedGroups never contains it.
    const visible = groups.some(
      (g) => (flatMode || expandedGroups[g.key]) && g.gags.some((x) => `${g.key}::${x.id}` === expandedKey)
    );
    if (!visible) {
      setExpandedKey(null);
      setOpeningKey(null);
      setClosingKey(null);
      setDirtyKey(null);
      // The row-edit draft goes with it, for the same reason: a draft that
      // outlived its row would keep `panelDirty` true forever and block every
      // future collapse, with no visible row to explain why.
      //
      // 🟢 In practice the FREEZE above makes this unreachable for a DIRTY
      // row — a frozen group structure cannot drop the row being edited — so
      // what this clears is a clean, untouched draft. Kept unconditional
      // anyway: relying on the freeze would make this a premise rather than a
      // guarantee, and premises expire.
      closeRowEditor();
    }
  }, [groups, expandedGroups, expandedKey, flatMode]);

  // ── Deep-expand from a ?gag= link (Round 5, My Tasks → Breakdown) ──
  // Expand the FIRST group containing the gag, then its row, then scroll it
  // into view. Two-step by design: opening the group re-renders, this effect
  // re-runs, and the row expansion happens on the next pass. The page owns
  // the not-found / filter-reset logic — we only act when the gag is here.
  const onDeepExpandDoneRef = useRef(onDeepExpandDone);
  useEffect(() => { onDeepExpandDoneRef.current = onDeepExpandDone; });
  useEffect(() => {
    if (deepExpandGagId == null) return;
    const group = groups.find((g) => g.gags.some((x) => x.id === deepExpandGagId));
    if (!group) return; // page decides (reset filters / give up)
    if (!flatMode && !expandedGroups[group.key]) {
      onToggleGroup(group.key); // step 1 — effect re-runs once state lands
      return;
    }
    const rowKey = `${group.key}::${deepExpandGagId}`;
    const scrollToRow = () => setTimeout(() => {
      findRowEl(rowKey)?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }, 120);
    if (expandedKey !== rowKey) {
      // Seed the inline editor on this path too — a row reached from a
      // `?gag=` link must behave exactly like one reached by clicking it
      // (expandRow: same server-computed gate, no re-derivation).
      const deepGag = group.gags.find((x) => x.id === deepExpandGagId);
      // 🔀 2026-09-29: a dirty panel ASKS, then opens the linked gag on Save
      // or Discard (was: pulse and give up). The pending id is consumed either
      // way — the held action carries the row, not the page's state.
      if (expandedKey && panelDirty) {
        continueWhenClean(() => { expandRowRef.current(rowKey, deepGag); scrollToRow(); });
        onDeepExpandDoneRef.current?.();
        return;
      }
      expandRow(rowKey, deepGag);
    }
    scrollToRow(); // after the expansion paints
    onDeepExpandDoneRef.current?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepExpandGagId, groups, expandedGroups, flatMode]);

  const startAssign = (gag) => {
    setAssigningId(gag.id);
    setAssignTo(gag.owner_id != null ? String(gag.owner_id) : '');
  };

  const confirmAssign = (gag) => {
    if (!assignTo) return;
    onAssign(gag, parseInt(assignTo, 10));
    setAssigningId(null);
  };

  // Row actions now live in a kebab (⋮) dropdown — only the actions valid for
  // the row's state + the caller's permissions are listed (same set/logic the
  // inline buttons used). Each item carries a title describing what the VERB
  // does. Assign still opens the inline owner picker (below) in the cell.
  // `card` (phone, 2026-10-02): a row with NO permitted action renders NOTHING
  // on a card instead of the table's muted "—". In the table the dash sits
  // under an "Actions" heading that explains it; on a card it sat alone beside
  // the title, and beside cards that DID show ⋮ it read as a broken menu
  // (observed as a plain user: ⋮ on the one unowned gag — Take — and "—" on
  // every gag someone else owns). Same gates, same items; only the empty case
  // differs, so no card offers a menu the server would refuse.
  const renderActions = (gag, { card = false } = {}) => {
    if (assigningId === gag.id) {
      return (
        <div className="flex items-center gap-1.5">
          <select
            value={assignTo}
            onChange={(e) => setAssignTo(e.target.value)}
            aria-label="Assign owner"
            title="Choose who should own this gag"
            className="px-2 py-1 text-xs rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200"
          >
            <option value="">Select user…</option>
            {/* Current user first for quick self-assign (replaces the old Take
                action for managers), then everyone else. */}
            {[
              ...deptUsers.filter((u) => u.id === userId),
              ...deptUsers.filter((u) => u.id !== userId),
            ].map((u) => (
              <option key={u.id} value={u.id}>
                {u.display_name}{u.id === userId ? ' (me)' : ''}
              </option>
            ))}
          </select>
          <button type="button" onClick={() => confirmAssign(gag)} disabled={!assignTo} className={BTN_XS.confirm}
            title="Confirm the assignment">
            OK
          </button>
          <button type="button" onClick={() => setAssigningId(null)} className={BTN_XS.secondary}
            title="Cancel without assigning">Cancel</button>
        </div>
      );
    }

    const items = [];
    if (deletedView) {
      // Mirror the server's restore guard (deleter or manager) — the list
      // payload's SELECT g.* includes deleted_by, so we can gate client-side.
      const canRestore = canManage || gag.deleted_by === userId;
      if (canRestore) {
        items.push({ key: 'restore', label: 'Restore', title: 'Restore this gag from deleted',
          onClick: () => onRestore(gag) });
      }
      if (canManage) {
        items.push({ key: 'harddelete', label: 'Delete permanently', danger: true,
          title: 'Permanently destroy this gag and its unattached tasks — cannot be undone',
          onClick: () => onHardDelete(gag) });
      }
    } else {
      const isOwner = gag.owner_id === userId;
      // 🔻 TAKE IS DELETED (owner ruling 2026-10-02: "No, managers assign").
      // It was offered ONLY to non-managers, and `POST /api/gags/:gagId/take`
      // now refuses exactly those callers (breakdown rights in the gag's
      // department or 403) — a control the server refuses. Managers self-claim
      // via Assign… (their own name sits at the top of the picker).
      if (canManage) {
        items.push({ key: 'assign', label: 'Assign…', title: 'Assign an owner (managers only)',
          onClick: () => startAssign(gag) });
      }
      if (gag.owner_id != null && (isOwner || canManage)) {
        items.push({ key: 'release', label: 'Release', title: 'Release back to the unassigned pool',
          onClick: () => onRelease(gag) });
      }
      if (isOwner || canManage) {
        items.push({ key: 'delete', label: 'Delete', danger: true,
          title: 'Move to deleted gags (restorable)', onClick: () => onSoftDelete(gag) });
      }
    }
    // No valid actions for this row/permission combo → nothing to show.
    if (items.length === 0) {
      return card ? null : <span className="text-gray-300 dark:text-gray-600 text-xs">—</span>;
    }
    return <KebabMenu items={items} label="Gag actions" />;
  };

  // Group key of the expanded row. rowKey = `${group.key}::${gag.id}` and
  // gag.id is numeric (never contains '::'), so lastIndexOf('::') always
  // splits at the id delimiter — even when the group-key portion itself
  // contains '::' (scene group keys embed user-entered scene text). A
  // startsWith prefix test would mis-match in that case; exact === on the
  // derived key cannot.
  const expandedGroupKey =
    expandedKey != null ? expandedKey.slice(0, expandedKey.lastIndexOf('::')) : null;

  // ── Per-gag row model — the single source for BOTH tables ──
  // The desktop table row and the phone table row show the same gags with the
  // same behaviour, so the key, the expansion/fade/dimmed state and the
  // click handler are derived ONCE here and consumed by both maps. Nothing
  // below re-derives any of it.
  const rowModels = (group) => group.gags.map((gag, gagIdx) => {
    // Rows are keyed `${group.key}::${gag.id}` — under scene grouping the
    // same gag appears in several groups, and only the CLICKED one expands.
    const rowKey = `${group.key}::${gag.id}`;
    const isRowExpanded = expandedKey === rowKey;
    // Mid-fade (opening or closing) both renders go transparent.
    const isFading = closingKey === rowKey || openingKey === rowKey;
    return {
      gag,
      rowKey,
      isRowExpanded,
      isLastInGroup: gagIdx === group.gags.length - 1,
      // Panel stays MOUNTED through the 200ms fade-out (closingKey).
      expandedActive: isRowExpanded || closingKey === rowKey,
      closing: closingKey === rowKey,
      isFading,
      fadeClass: isFading ? 'opacity-0' : 'opacity-100',
      // Deleted rows stay non-clickable — editing a soft-deleted gag 409s
      // server-side; restore first.
      rowClick: deletedView ? undefined : () => toggleRow(rowKey, gag),
      rowTitle: deletedView ? undefined : isRowExpanded ? 'Click to collapse' : 'Click to expand',
      // Dimmed while ANOTHER row's panel holds unsaved edits.
      dimmed: !!(panelDirty && expandedKey && !isRowExpanded),
    };
  });

  /**
   * The inline row editor's Save / Discard bar.
   *
   * 📍 **IT SITS AT THE TOP OF THE EXPANDED AREA, DIRECTLY UNDER THE ROW IT
   * SAVES — not in the Actions column and not at the bottom of the panel.**
   * The Actions column is 60px, which cannot hold two buttons; the bottom of
   * `GagDetailPanel` is a long scroll away, which is the exact complaint that
   * turned the ORDER pages' collapse guard into a prompt. Under the row is
   * where the pointer already is, having just clicked the chevron.
   *
   * ⚠️ **PROTOTYPE-GRADE, and named as such in the report.** It is a plain
   * bar: no keyboard shortcuts (Enter-to-save / Escape-to-discard), no
   * per-field revert, no optimistic update. Those are phase 2.
   *
   * 🔑 It renders only while a draft exists, i.e. only when the server said
   * `can_edit === true`. It renders no controls at all otherwise — a viewer
   * without rights sees the panel exactly as they did before.
   */
  const renderRowEditBar = (gag, rowKey) => {
    if (editKey !== rowKey || editDraft == null) return null;
    // 🔀 2026-10-18 (owner: *"keep them and make them save the work as
    // well"*): the bar is live when ANYTHING under this gag is dirty — the
    // shared draft OR an open inline task edit (the panel's dirt under a shared
    // draft IS the task's). The label names its scope: "Save all" whenever a
    // task is in it. The ONE chip lives here; the task editor shows none.
    const taskDirty = dirtyKey === rowKey;
    const barDirty = rowDirty || taskDirty;
    const barBusy = savingRow || savingAll;
    return (
      <div
        data-testid={`gag-row-edit-bar-${gag.id}`}
        onClick={(e) => e.stopPropagation()}
        // Enter/Escape work from the bar's own buttons too. Attached HERE and
        // not to the whole expanded `<tr>`: that row also hosts GagDetailPanel,
        // whose own controls and dirty guard are a separate concern — an
        // Escape while typing in the panel must not raise the ROW's prompt.
        onKeyDown={(e) => rowEditKeyDown(e, rowKey, gag)}
        className={ROW_EDIT_BAR_CLASS}
      >
        <span className={ROW_EDIT_BAR_LABEL_CLASS}>
          Editing this row
        </span>
        {barDirty && (
          // The amber "unsaved" chip. Shares the pulse the dirty guard fires,
          // so a blocked collapse/view-switch draws the eye to the thing
          // blocking it.
          <span
            data-testid={`gag-row-unsaved-${gag.id}`}
            className={`${PILL} ${ROW_EDIT_UNSAVED_CHIP_CLASS} ${
              showDirtyWarning ? 'animate-pulse' : ''
            }`}
          >
            ⚠ Unsaved changes
          </span>
        )}
        {rowSaveError && (
          <span role="alert" data-testid={`gag-row-edit-error-${gag.id}`}
            className="text-xs text-red-600 dark:text-red-400">
            {rowSaveError}
          </span>
        )}
        <span className="ml-auto flex items-center gap-2">
          <button
            type="button"
            className={BTN_XS.secondary}
            data-testid={`gag-row-discard-${gag.id}`}
            disabled={barBusy}
            // Re-seed from the SAME base object the draft was taken from, so
            // discard restores what the user opened rather than whatever a
            // background refresh has since produced.
            // Reverts the WHOLE shared draft — row AND panel fields (2026-09-29)
            // — and, after asking, an open task edit (2026-10-18).
            onClick={() => requestBarDiscard(rowKey)}
            title={taskDirty
              ? 'Discard every unsaved change to this gag and to the task being edited'
              : 'Discard every unsaved change to this gag, here and in the details below'}
          >
            Discard
          </button>
          <button
            type="button"
            className={BTN_XS.confirm}
            data-testid={`gag-row-save-${gag.id}`}
            disabled={barBusy || !barDirty}
            onClick={saveFromBar}
            title={taskDirty
              ? 'Save this gag, then the task being edited'
              : 'Save every unsaved change to this gag, here and in the details below'}
          >
            {barBusy ? 'Saving…' : taskDirty ? 'Save all' : 'Save gag'}
          </button>
        </span>
      </div>
    );
  };

  /**
   * 📱 ONE PHONE ROW (2026-10-03 prototype) — a row model from `rowModels`
   * rendered against `GAG_PHONE_TABLE_COLUMNS`, plus (while expanded) the
   * shared `ExpandedPanelRow` holding the SAME `GagDetailPanel`. `rowClick` is
   * `toggleRow`, so single-expand and the dirty guard are the desktop's own.
   * The Assign picker (`renderActions` while assigning) cannot fit the 40px
   * kebab column, so it opens on a full-width row under the gag.
   */
  const renderPhoneRow = ({
    gag, rowKey, isRowExpanded, expandedActive, closing, fadeClass, rowClick, rowTitle, dimmed,
  }, phonePanelWidth) => {
    const TDP = 'h-11 py-1.5 align-middle text-sm text-gray-700 dark:text-gray-300 overflow-hidden';
    const sub = [gag.scenes_display ? `Sc ${gag.scenes_display}` : null, gag.sub_department_name]
      .filter(Boolean).join(' · ');
    const assigning = assigningId === gag.id;
    const muted = (v) => (v ? <span className="truncate block">{v}</span>
      : <span className="text-gray-400 dark:text-gray-500">-</span>);
    const TDT = `${TDP} px-2 truncate`;
    // Display-only cells (owner change 2026-10-03): editing stays in the panel.
    const cells = {
      title: (
        <td key="title" className={`${TDP} pl-1 pr-1`} data-col="title">
          <div className="flex items-center gap-1 min-w-0">
            <span className="shrink-0"><RowExpandChevron expanded={isRowExpanded} /></span>
            <div className="min-w-0">
              <div className="truncate leading-tight font-medium text-gray-800 dark:text-gray-200" title={gag.title}>
                {gag.title}
              </div>
              {sub && (
                <div className="truncate text-xs leading-tight text-gray-500 dark:text-gray-400" title={sub}
                  data-testid={`gag-phone-sub-${gag.id}`}>
                  {sub}
                </div>
              )}
            </div>
          </div>
        </td>
      ),
      shoot: (
        <td key="shoot" className={`${TDP} px-1`} data-col="shoot"
          title={[shootDateTooltip(gag), gag.shoot_date ? formatDate(gag.shoot_date) : null].filter(Boolean).join(PHONE_TITLE_SEP) || undefined}>
          {gag.shoot_date ? (
            <span className="inline-flex items-center gap-1 max-w-full">
              <DueIndicator state={dueStatus(gag.shoot_date, gag.status)} titleOnParent />
              <span className="truncate min-w-0">{formatDateCompact(gag.shoot_date)}</span>
            </span>
          ) : (
            <span className="text-xs text-gray-500 dark:text-gray-400">No date</span>
          )}
        </td>
      ),
      status: (
        <td key="status" className={`${TDP} px-1`} data-col="status" title={GAG_STATUS_LABELS[gag.status] || gag.status || undefined}>
          <StatusPill status={gag.status} fit />
        </td>
      ),
      actions: (
        <td key="actions" className={`${TDP} px-0 text-center`} data-col="actions" onClick={(e) => e.stopPropagation()}>
          {assigning ? null : renderActions(gag, { card: true })}
        </td>
      ),
      owner: (
        <td key="owner" className={TDT} data-col="owner" title={gag.owner_name || 'Unassigned'}>
          {gag.owner_id == null
            ? <span className="text-xs text-gray-500 dark:text-gray-400">Unassigned</span>
            : muted(String(gag.owner_name || '').trim().split(/\s+/)[0])}
        </td>
      ),
      sub_department: (
        <td key="sub_department" className={TDT} data-col="sub_department" title={gag.sub_department_name || ''}>
          {muted(gag.sub_department_name)}
        </td>
      ),
      scenes: (
        <td key="scenes" className={TDT} data-col="scenes" title={gag.scenes_display || ''}>{muted(gag.scenes_display)}</td>
      ),
      day_night: (
        <td key="day_night" className={TDT} data-col="day_night">{muted(DAY_NIGHT_LABELS[gag.day_night])}</td>
      ),
      int_ext: (
        <td key="int_ext" className={TDT} data-col="int_ext">{muted(INT_EXT_LABELS[gag.int_ext])}</td>
      ),
      location: (
        <td key="location" className={TDT} data-col="location" title={gag.location || ''}>{muted(gag.location)}</td>
      ),
      unit: (
        <td key="unit" className={TDT} data-col="unit" title={gag.unit || ''}>{muted(gag.unit)}</td>
      ),
      priority: (
        <td key="priority" className={`${TDP} px-1`} data-col="priority"
          title={TASK_PRIORITY_LABELS[gag.priority] || gag.priority || undefined}>
          <PriorityPill priority={gag.priority} fit />
        </td>
      ),
      ra: (
        <td key="ra" className={`${TDP} px-1 text-center`} data-col="ra">{renderRaIcon(gag.ra_status)}</td>
      ),
      progress: (
        <td key="progress" className={`${TDP} px-2`} data-col="progress"
          title={gag.task_total > 0 ? `${gag.task_done}/${gag.task_total} tasks done` : undefined}>
          <GagProgress gag={gag} barClassName="w-12 min-w-[40px]" />
        </td>
      ),
    };
    return (
      <React.Fragment key={rowKey}>
        <tr
          data-rowkey={rowKey}
          data-testid={`gag-phone-row-${gag.id}`}
          data-mobile-row=""
          onClick={rowClick}
          title={rowTitle}
          className={`${rowClick ? 'cursor-pointer select-none' : ''} transition-colors ${isRowExpanded ? EXPANDED_ROW_FRAME : ROW_HOVER} ${dimmed ? 'opacity-40' : ''}`}
        >
          {GAG_PHONE_TABLE_COLUMNS.map((c) => cells[c.key] || null)}
        </tr>
        {assigning && (
          <tr onClick={(e) => e.stopPropagation()} data-pan-skip="" data-expanded-panel="">
            <td colSpan={GAG_PHONE_TABLE_COLUMNS.length} className="px-2 py-2 bg-gray-50 dark:bg-gray-700/30">
              {renderActions(gag)}
            </td>
          </tr>
        )}
        {expandedActive && isNarrow && (
          <ExpandedPanelRow
            colSpan={GAG_PHONE_TABLE_COLUMNS.length}
            panelWidth={phonePanelWidth}
            testId={`gag-phone-panel-${gag.id}`}
            isLoading={false}
            detail={gag}
          >
            <div
              className={`grid transition-[grid-template-rows,opacity] duration-200 ease-out ${
                closing ? 'grid-rows-[0fr]' : 'grid-rows-[1fr]'
              } ${fadeClass}`}
            >
              <div className={`min-h-0 min-w-0 ${closing ? 'overflow-hidden' : ''}`}>
                {renderPanel(gag, rowKey)}
              </div>
            </div>
          </ExpandedPanelRow>
        )}
      </React.Fragment>
    );
  };

  // The expanded editor — identical props from the table row and the card,
  // except `rowEditedFields` (2026-10-18, owner: the row edits the row's
  // items, Gag details the additional ones). Passed ONLY from the DESKTOP
  // row (`desktopRow`) and only while that row has a draft — the one place the
  // row renders controls. The phone row's draft exists too (`expandRow` →
  // `openRowEditor` has no width check) but its cells are display-only, so the
  // phone panel stays complete. Derived from the row's own predicate.
  const renderPanel = (gag, rowKey, { desktopRow = false } = {}) => (
    <GagDetailPanel
      rowEditedFields={desktopRow && !isNarrow && editKey === rowKey && editDraft != null
        ? gagRowControlledPanelFields({ canAssignOwner: canManage })
        : null}
      gag={gag}
      userId={userId}
      subDepts={subDepts || []}
      showDirtyWarning={showDirtyWarning}
      onDirtyChange={(d) => handlePanelDirty(rowKey, d)}
      onChanged={onGagUpdated}
      // Only the EXPANDED row's panel publishes — a closing panel fading out
      // beside a freshly opened one must never answer for it.
      submitRef={rowKey === expandedKey ? panelSubmitRef : undefined}
      discardRef={rowKey === expandedKey ? panelDiscardRef : undefined}
      // 🔑 ONE DRAFT (2026-09-29): the open row's draft IS the panel's form —
      // an edit in either place shows in the other, one dirty state, one Save.
      // Absent (no row draft: the server did not mark `can_edit`, or the
      // deleted view) the panel keeps its own form, as before.
      // Owner 2026-10-05: the desktop Table KEEPS its "Editing this row" bar as
      // the gag's save, so the panel's centred pinned bar is phone + Timeline
      // only wherever that bar exists (i.e. the row has a draft).
      desktopSaveBar={!(editKey === rowKey && editDraft != null)}
      sharedDraft={editKey === rowKey && editDraft != null ? {
        form: panelFormFromDraft(editDraft),
        dirty: rowDirty,
        saving: savingRow,
        error: rowSaveError,
        savedSignal: draftSavedSignal,
        onChange: setDraftFromPanel,
        onSave: () => saveRowEdit(gag),
        onDiscard: discardDraft,
      } : null}
      onLinkNavigate={onLinkNavigate
        ? (to) => continueWhenClean(() => onLinkNavigate(to))
        : undefined}
    />
  );

  return (
    /**
     * ══════════════════════════════════════════════════════════════════
     * ONE PAGE SCROLL, PINNED GROUP HEADS (2026-09-26). Product owner, after
     * the order tables moved: *"yh this worked well do all other similar
     * pages"* — the Breakdown first. It replaces THE ONE SCROLL REGION of
     * 2026-08-08 (an element owning both axes, height-capped at the viewport,
     * with every sticky title and `<th>` pinning INSIDE it): the owner has ruled
     * a scroll window inside a scrolling page out (*"a scroll within a scroll?
     * not nice UI desing"*). 🅱️ in `constants/stickyTable.js`.
     * ══════════════════════════════════════════════════════════════════
     *
     * 🚨 **THE FACT THAT STILL DECIDES THE SHAPE:** a sticky header cannot pin to
     * the page from inside a horizontally scrollable box (`overflow-x: auto`
     * makes an element a scroll container whatever its y-axis does). The
     * per-group `overflow-x-auto` of 2026-08-08 made every `<th>` inert for
     * exactly that reason; it must not come back AROUND A HEADER.
     *
     * ✅ **SO EACH GROUP IS SPLIT, NOT WRAPPED:**
     *   1. its HEAD — `sticky`, `top: groupHeaderTop` against the PAGE — holds
     *      the group title (outside any horizontal scroller, so it is only ever
     *      as wide as the visible card) and a header-only `<table>` in its own
     *      x-scroll pane. Title and column headers pin as ONE block: nothing can
     *      scroll between them and no offset between them can be wrong. The
     *      head's containing block is its group CARD, so it releases at the
     *      card's end and the NEXT group's head takes over — the outgoing head
     *      is pushed up by its own card's bottom, never overlapped.
     *   2. its ROWS — an x-only pane (`PAGE_SCROLL_PANE_X`: `overflow-y:
     *      hidden`, NO height cap) that scrolls with the page. It is the
     *      scrollport the frozen cells and the `sticky left-0` expanded panel
     *      resolve against, and `relative` (`STICKY_REGION_CONTAINMENT`) so no
     *      absolutely-positioned descendant resolves against the ICB.
     *   3. ONE sticky-bottom bar for the whole list (`ui/StickyScrollbar`), shown
     *      while the table overflows — the 2026-08-10 ruling *"THe horizontal
     *      scroll bars iin table views on al pages should be sticky"* binds.
     * Every header pane, every rows pane and the bar carry
     * `data-page-scroll-pane` and are linked two-way by
     * `hooks/useLinkedScrollLeft`, so ALL groups still scroll sideways together.
     * Alignment is the ONE width map: every pane's table gets the same
     * `<colgroup>` and the same `minWidth`, inside panes of equal width.
     *
     * 🔑 **This element is NOT a scroll container** — it is the measured width
     * (`rootRef`, the visible content box) and the bar's containing block.
     *
     * ♿ Two `<table>`s per group, re-joined for a screen reader exactly as
     * `OrderTableShell` does it: the CARD is `role="table"` (md and up), the
     * header table `role="none"` with explicit `rowgroup`/`row`/`columnheader`,
     * the body table `role="rowgroup"`.
     *
     * 📱 **Below `md` the head holds only the title and pins at the SAME
     * `groupHeaderTop`** (fixed 2026-09-27, owner). The table is `hidden
     * md:block` and the card list `md:hidden`. It pinned at `top: 0` until then,
     * where `ui/Toolbar` also pins below `lg` and, at `z-20` over the head's
     * `z-10`, covered it — measured at 375px: toolbar 0–113px, a stuck title
     * 0–44px, `elementFromPoint` on the title hit the toolbar. `groupHeaderTop`
     * is `useStickyToolbar`'s measured `headerTop` (app bar 0 below `lg` +
     * toolbar height), so it already is "directly under the toolbar" here.
     *
     * ⚠️ jsdom applies no stylesheet and does no layout: the tests pin the
     * structure, the classes and the `top`. **Whether a head pins, hands over to
     * the next group, stays aligned and the page stops double-scrolling are
     * BROWSER checks.**
     */
    <div
      ref={rootRef}
      className={STICKY_REGION_CONTAINMENT}
      data-testid="gag-group-scroll-region"
    >
    {/* 🔀 The list's `minWidth: totalWidth` is GONE (2026-09-26): the cards are
        now as wide as the VISIBLE region and only the panes inside them
        scroll, so a card no longer has to be as wide as its table. The
        percentage-width trap it existed to avoid (`md:w-max` → tables 10px
        apart) cannot recur: each table's `w-full` resolves against its pane,
        a definite width. */}
    <div className="space-y-3">
      {groups.map((group, groupIndex) => {
        // Flat mode's single pseudo-group has no header and is always open.
        const open = flatMode || !!expandedGroups[group.key];
        // Does the dirty expanded row live in THIS group?
        const dirtyRowInGroup = panelDirty && expandedGroupKey === group.key;
        // Built once, consumed by the desktop table AND the phone table —
        // and only for an OPEN group, since a collapsed group renders no
        // rows at all (the v1 scale strategy).
        const rows = open ? rowModels(group) : null;
        /**
         * 🔀 COLUMN HEADER CELLS IN THE PINNED HEAD (2026-09-26). Product owner,
         * 2026-08-08: *"breakdown sticky headder please."* — still delivered,
         * now by the group HEAD (title + header pane) pinning against the page
         * rather than by each `<th>` pinning inside an inner region.
         *
         * 🔑 **A non-frozen `<th>` no longer needs `sticky` or a `top`** — the
         * pane is what moves. It keeps `relative` + `z-[5]` + its fill because
         * that makes it a STACKING CONTEXT, which is what keeps a resize
         * handle's `z-10` trapped BELOW the frozen corner's `z-[6]` when its
         * column slides under the frozen block (without it the handle of a
         * scrolled-under column would paint over "Title"). The 2026-08-08 docs
         * record the same stacking fact for the old sticky cells.
         *
         * 🔀 **The 1px `SEAM_COVER_PX` and the measured group-title offset are
         * GONE**: title and headers are one sticky block, so no seam exists
         * between them to cover. See `groupHeadRef`.
         *
         * ── THE FROZEN HEADER CORNER (2026-08-09) ──────────────────────────
         * A frozen column's `<th>` pins on the LEFT inside the header pane,
         * mirroring its cells in the rows pane, so "Title" never slides away
         * from the Title column it names. `z-[6]` — one tier above its `z-[5]`
         * neighbours so the corner paints over the headings sliding under it
         * (equal z-index would lose on DOM order; the frozen columns come
         * first), still below the head's `z-10`. The fill is restated because a
         * sticky cell takes no background from its row. See
         * `constants/stickyTable.js`.
         */
        const headerCellFor = (columnKey) => {
          const frozen = columnKey in frozenLeft;
          if (!frozen) {
            return { className: `relative ${STICKY_COLUMN_HEADER_Z} ${GAG_TABLE_HEADER_BG}`, style: undefined };
          }
          return {
            className: `sticky ${STICKY_COLUMN_CORNER_Z} ${GAG_TABLE_HEADER_BG}`,
            style: { left: frozenLeft[columnKey] },
          };
        };
        /* 🔑 ONE width map, rendered into EVERY table's colgroup — the header
           pane's and the rows pane's of every group. Built fresh per table
           rather than shared as one element: `<colgroup>` must be the FIRST
           child of its own `<table>`, and one element instance cannot be in
           ten of them. */
        const colgroup = () => (
          <colgroup>
            {GAG_TABLE_COLUMNS.map((c) => (
              <col
                key={c.key}
                style={{ width: Number.isFinite(widths[c.key]) ? widths[c.key] : c.width }}
              />
            ))}
          </colgroup>
        );
        return (
          <div
            key={group.key}
            // ♿ md and up: ONE accessible table over this group's two DOM
            // tables (see the return's docblock). Below md the phone table
            // (its own PageScrollTable) carries the table role instead.
            role={open && !isNarrow ? 'table' : undefined}
            // 📱 Phone (owner, 2026-10-03): edge to edge — no side borders, no
            // rounding, and -mx-2 cancels the page p-2 so the table spans the screen.
            className={`relative bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl ${PHONE_FULL_BLEED_CARD}`}
            data-testid={`gag-group-card-${group.key}`}
          >
            {/* THE GROUP HEAD — title + header pane, pinned as ONE block
                against the PAGE at `groupHeaderTop` (md and up). Its
                containing block is this card, so it releases at the card's
                end and the next group's head takes over. `z-10` is the
                section tier: over the rows pane and its frozen cells, under
                the toolbar's `z-20`. Below md too — see the return's
                docblock. */}
            <div
              ref={groupIndex === 0 ? groupHeadRef : undefined}
              className={`sticky ${STICKY_SECTION_HEADER_Z}`}
              style={{ top: groupHeaderTop }}
              data-testid={`gag-group-head-${group.key}`}
            >
            {!flatMode && (
            <button
              type="button"
              aria-expanded={open}
              // 🪤 A testid rather than leaving tests to find this by
              // `button[aria-expanded]`: every ROW's expand chevron carries that
              // attribute too, so the bare selector matches a row control the
              // moment a group is expanded — silently, picking the wrong element
              // rather than failing.
              data-testid={`gag-group-header-${group.key}`}
              // 🔀 NOT sticky itself since 2026-09-26: the HEAD above is the
              // sticky element and carries this title with the column headers.
              // (2026-08-08 → 2026-09-26 it was `sticky` at `top: 0` of the inner
              // region; before that, `sticky` at `groupHeaderTop` of the page —
              // the offset is back, on the head.) Opaque bg so rows scroll
              // cleanly underneath.
              onClick={() => {
                // 🔀 2026-09-29: while anything is dirty a group-header click
                // ASKS (the three-way prompt), then toggles on Save or Discard
                // — was block-and-pulse for the dirty row's own group, and
                // `pointer-events-none` on every other group's header.
                if (panelDirty) {
                  continueWhenClean(() => onToggleGroupRef.current(group.key));
                  return;
                }
                onToggleGroup(group.key);
              }}
              className={`shadow-sm flex items-center gap-2 w-full text-left px-4 py-3 bg-gray-50 dark:bg-gray-700 min-h-[44px] md:min-h-0 ${open ? 'rounded-t-xl' : 'rounded-xl'} max-md:rounded-none ${
                // Consistent with dimmed rows: while a panel is dirty, other
                // groups' headers dim out — still CLICKABLE (a click asks).
                panelDirty && !dirtyRowInGroup ? 'opacity-40' : ''
              }`}
            >
              {/* 🔀 The label's `sticky left-0` (2026-08-08 → 2026-09-26) is
                  DELETED, not left inert: it existed because the card was as
                  wide as the widest TABLE inside one horizontal scroller, so
                  scrolling right carried the chevron and title off the left
                  edge. The title now sits OUTSIDE every horizontal scroller and
                  the card is only as wide as the visible region, so there is
                  nothing for it to pin against. */}
              <span className="flex w-fit items-center gap-2 bg-gray-50 dark:bg-gray-700">
                {/* Shared with My Tasks' group headers (`ui/GroupToggleLabel`). */}
                <GroupToggleLabel open={open} label={group.label} count={group.gags.length} />
              </span>
            </button>
            )}
            {/* THE HEADER PANE — a header-only `<table>` in its own x-scroller
                (bar hidden; linked to the rows and the bar). The fill is on
                the PANE because the pane is what moves over the rows. */}
            {open && (
              <div
                data-page-scroll-pane
                className={`hidden md:block ${STICKY_REGION_CONTAINMENT} ${PAGE_SCROLL_PANE_X} ${GAG_TABLE_HEADER_BG} border-b border-gray-200 dark:border-gray-700 ${flatMode ? 'rounded-t-xl' : 'border-t'}`}
                data-testid={`gag-group-header-pane-${group.key}`}
              >
                <table role="none" className="w-full table-fixed" style={{ minWidth: totalWidth }}>
                  {colgroup()}
                  <thead role="rowgroup">
                    <tr role="row">
                      {/* 🔀 ADOPTED `components/ui/SortableHeader` 2026-08-08.
                          Product owner: *"breakdown needs the always show the up
                          down filter arrows for the table view. Please make the
                          table view layout a single piece of code or class that
                          can be used on all table pages so we dont diverge ever
                          again"*.

                          WHAT HE ACTUALLY GETS, and it is smaller than it looks:
                          these headers were ALREADY clickable and already sorted.
                          What was missing was the always-visible caret — this
                          table rendered `''` for an inactive column, so a user
                          could not tell a sortable heading from a fixed one
                          without clicking it. The shared cell renders the
                          neutral `↕` instead, and brings the `w-3` caret box,
                          the `p-0`, the `shrink-0` and the opacity-not-colour
                          hover with it. All four are measured behaviours the
                          order table learned the expensive way and this table
                          never had.

                          🔑 ALL 13 DATA COLUMNS ARE SORTABLE and were already —
                          `HEADER_SORT` covers every one. The two that are NOT
                          are the expand chevron and the Actions kebab, which are
                          CONTROLS rather than data, exactly like the order
                          table's `expand`. So the owner's "all columns can be
                          sortable" is already satisfied; nothing needed adding.

                          🟢 AND THE SILENT-WRONG-KEY HAZARD DOES NOT EXIST HERE.
                          The order pages look a sort field up as `a[sortField]`,
                          so a name no row carries sorts nothing and leaves a
                          live-looking header. This table dispatches through
                          `PRIMARY_COMPARATORS`, a MAP of explicit comparators —
                          an unrecognised id has no comparator at all and falls
                          back to `shoot`. Its comparators are also better than a
                          generic string sort: `status`/`priority` order by the
                          defined workflow sequence rather than alphabetically,
                          blanks go last via `nullsLast` on every column EXCEPT
                          `shoot`, which sorts them FIRST (owner ruling
                          2026-09-22 — see `nullsFirst`), and `scene`
                          resolves a gag's MULTIPLE scenes to its earliest
                          `scene_sort` — a defined answer to the multi-valued
                          question, not an accident. Nothing about the sorting
                          changed in this commit. */}
                      {/* 🔀 ITERATES THE COLUMN MODEL, NOT THE LABELS, SINCE
                          2026-08-09 — a header cell now needs the column's
                          `key`, `min` and live width as well as its label and
                          sort id, and looking three of those up BY LABEL would
                          make two columns sharing a heading string silently
                          share a width floor. */}
                      {GAG_TABLE_COLUMNS.map((c) => {
                        // Frozen columns get the both-axes corner treatment;
                        // every other column gets exactly what it got before.
                        const headerCell = headerCellFor(c.key);
                        return (
                        <SortableHeader
                          key={c.key}
                          role={isNarrow ? undefined : 'columnheader'}
                          label={c.label}
                          sortId={c.sortId}
                          activeSort={flatSort}
                          sortDir={sortDir}
                          onSort={onHeaderSort}
                          className={headerCell.className}
                          style={headerCell.style}
                          ariaLabel={!c.label ? 'Expand' : undefined}
                          // 2026-09-27 — a column may be squeezed below its
                          // heading (`Fit columns to screen`), so the label
                          // ellipses; the full text stays on the sort title.
                          truncateLabel
                          // 2026-09-27 — D/N's `fullLabel` ("Day/Night") names
                          // the column in the sort sentence; every other column
                          // has none, so `sortLabel` is `undefined` and the
                          // sentence falls back to `c.label` unchanged.
                          sortLabel={c.fullLabel}
                        >
                          {/* 🚨 A CHILD OF THE `<th>`, A SIBLING OF THE SORT
                              BUTTON. That is what makes a drag structurally
                              unable to fire a sort — see `ui/SortableHeader`
                              note 8. Moving it inside the button will read as a
                              tidy-up and will sort the table on every drag. */}
                          {resizable ? (
                            <ColumnResizeHandle
                              columnKey={c.key}
                              // The expand chevron has no heading text, so the
                              // handle borrows the same name the cell already
                              // announces rather than saying "Resize  column".
                              label={c.label || 'Expand'}
                              width={Number.isFinite(widths[c.key]) ? widths[c.key] : c.width}
                              min={c.min}
                              max={MAX_GAG_COLUMN_WIDTH}
                              totalWidth={totalWidth}
                              // 🔑 MEASURED, never assumed. The drag undoes the
                              // table's stretch-to-fit with it; `null` before
                              // the first measurement means "assume no stretch",
                              // which is the right answer for every table that
                              // already overflows its scrollport.
                              scrollportWidth={panelWidth == null ? null : panelWidth - 2}
                              onResize={onColumnResize}
                              onReset={onColumnResetWidth}
                              testIdPrefix="gag-col-resize"
                            />
                          ) : null}
                        </SortableHeader>
                        );
                      })}
                    </tr>
                  </thead>
                </table>
              </div>
            )}
            </div>

            {/* Rows render only when the group is expanded — v1 scale strategy.
                Below md this table is `hidden md:block` and the phone table
                renders the SAME row models. */}
            {open && (
            <>
              {/* THE ROWS PANE — x-only, on the PAGE's vertical scroll: no
                  `max-height`, `overflow-y: hidden` (never `auto`: nothing here
                  may become a second vertical scroller). `relative` so the
                  frozen cells, the `sticky left-0` expanded panel and any
                  absolutely-positioned child resolve INSIDE it. 🔀 The
                  2026-08-08 rule "no `overflow-x-auto` here" was about a
                  scroller wrapped AROUND the `<th>`s; the headers now live in
                  their own pane above, so this one may and must scroll. */}
              <div
                data-page-scroll-pane
                className={`hidden md:block ${STICKY_REGION_CONTAINMENT} ${PAGE_SCROLL_PANE_X} rounded-b-xl`}
                data-testid={`gag-group-rows-${group.key}`}
              >
                {/* ══════════════════════════════════════════════════════
                    🔴 `table-fixed` + A SHARED `<colgroup>` (2026-08-09)
                    ══════════════════════════════════════════════════════
                    This table was AUTO-LAYOUT with no `<colgroup>` until
                    inline row editing landed, and CLAUDE.md recorded the
                    consequence as owed work: **N tables, one per group, each
                    sizing its own columns to its own content, so the widths
                    already differed between groups** — visible since they were
                    forced to a common width inside one scroll region.

                    Inline editing does not merely inherit that problem, it
                    makes it acute: a `<select>`/`<input>` carries its own
                    intrinsic minimum width, so controls dropped into
                    auto-layout cells would resize the columns of whichever
                    group holds the expanded row, and only that group.

                    🔑 **THE WIDTHS COME FROM ONE ARRAY, SHARED BY EVERY GROUP
                    TABLE** (`constants/gagTableColumns.js`). Per-group widths
                    would un-align the group cards, which is the exact symptom
                    the `md:w-max` change existed to remove.

                    🪤 **`w-full` + `minWidth` IS CORRECT HERE ONLY BECAUSE THE
                    LIST ABOVE CARRIES A DEFINITE `minWidth`. Two measured
                    failures got us here and both are worth knowing, because
                    the first one LOOKED FINE by the obvious check.**
                    (1) With the list still at `md:w-max` (content-sized) and
                    the table at `w-full`, the percentage asks for 100% of a
                    parent that is sizing itself to that child — a circular
                    constraint Chromium resolved at a table **1,000,000px
                    wide**. Every column stretched proportionally, so the five
                    groups still aligned *perfectly with each other* and a
                    pure alignment assertion reported SUCCESS.
                    (2) Swapping the table to `min-w-full` removed the absurd
                    width but not the cycle: the tables came out 1727 / 1721 /
                    1717 / 1717 / 1721 px inside five identical 1729px cards,
                    i.e. columns up to 10px out between groups.
                    ✅ The fix is on the PARENT, not here — see the list's own
                    note. Give the list a definite width and every card,
                    wrapper and table agrees, `w-full` fills the scrollport,
                    and the `minWidth` keeps the floor so a narrow screen
                    scrolls horizontally inside the region instead of crushing
                    13 data columns. **Do not "restore" `w-max` above.**

                    ⚠️ **THIS EXPIRES A DOCUMENTED PREMISE, corrected in
                    CLAUDE.md in the same commit.** `ui/SortableHeader`'s entry
                    said the order table's *"a sortable header must FIT its
                    column"* rule *"has no meaning on the Breakdown … a header
                    cannot overflow a width it defines itself"*. True only
                    while this table was auto-layout. From here the fit rule
                    applies to all 13 data columns, and the declared widths
                    were measured with the caret accounted for. */}
                <table
                  role={isNarrow ? undefined : 'rowgroup'}
                  className="w-full table-fixed divide-y divide-gray-200 dark:divide-gray-700"
                  style={{ minWidth: totalWidth }}
                >
                  {colgroup()}
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {rows.map(({
                      gag, rowKey, isRowExpanded, isLastInGroup, expandedActive,
                      closing, isFading, fadeClass, rowClick, rowTitle, dimmed,
                    }) => {
                      // ── Expanded-pair separation (Pete's feedback) ──
                      // The summary row + panel must read as ONE lifted card,
                      // clearly separate from neighbouring gags. Tables don't
                      // honour tr margins, so slim spacer rows open the gap
                      // above and below the pair. They share the panel's fade
                      // lifecycle exactly: same render condition
                      // (expandedActive) and they animate HEIGHT on the same
                      // openingKey/closingKey signals the panel animates
                      // opacity on — no orphan gap can outlive the panel
                      // (both read the shared row model's isFading).
                      const spacerHeights = `transition-[height] duration-200 ease-out ${
                        isFading ? 'h-0' : 'h-14'
                      }`;
                      // Gap MASK (Pete: the expanded card must FLOAT free —
                      // no card borders/bg crossing the gaps). The mask is
                      // absolutely positioned with the GROUP CARD as its
                      // containing block (the card is `relative`, nothing in
                      // between is positioned), which does two jobs at once:
                      //   1. it ESCAPES the overflow-x scrollport's clip, and
                      //   2. left/right resolve against the CARD, not the
                      //      scrolled table — so it always spans the card's
                      //      visible width (tracks the scroll container's
                      //      edges, not the content width).
                      // The 2px overhang (-left/right-0.5) covers the card's
                      // own 1px vertical borders; the fill is the PAGE
                      // background (bg-gray-100; the global html.dark
                      // override maps it to the dark page colour, slate-800
                      // kept explicit for clarity). Vertical position is the
                      // STATIC position (top:auto) — i.e. exactly the spacer
                      // slot — and it animates the same height classes as
                      // the flow spacer, so it can never outlive the gap.
                      const gapMask =
                        `absolute -left-0.5 -right-0.5 ${spacerHeights} bg-gray-100 dark:bg-slate-800`;
                      // Card frame: blue top edge across the summary row (the
                      // matching bottom edge lives on the panel div). NO
                      // left/right accents — Pete: the old 4px blue accent sat
                      // inside the group card's own 1px border and read as TWO
                      // vertical lines on the left. The card border can't be
                      // suppressed per-segment (one continuous element around
                      // the whole group), so the block's sides use that single
                      // card border — exactly one line each side, symmetric
                      // with the right edge he praised. Cell-level borders,
                      // not a tr ring/box-shadow — reliable under the table's
                      // border-collapse; keeps the [&>td] bg convention.
                      // Expanded rows stay clickable (they collapse), so they
                      // keep a hover step ABOVE the static highlight — otherwise
                      // there's no affordance signalling they can be collapsed.
                      // tr-level hover:bg can't show through the [&>td] cell bg,
                      // so the hover targets the cells — and the :hover must sit
                      // on the ROW ([&:hover>td] → tr:hover>td), NOT on each cell
                      // (hover:[&>td] compiles to >td:hover = per-column), so the
                      // WHOLE row lights up as one.
                      // 🔀 2026-10-25: the shared `EXPANDED_ROW_FRAME` (this was a
                      // verbatim local copy of it; the phone row already used it).
                      const expandedFrame = EXPANDED_ROW_FRAME;

                      // ── Inline row editing ──────────────────────────
                      // `rowEditing` is true only when a draft exists for THIS
                      // row, and a draft only exists when the SERVER said
                      // `can_edit === true`. There is no editability test here.
                      const rowEditing = editKey === rowKey && editDraft != null;
                      // Owner 2026-09-29: the edited row's cells sit at HALF the
                      // gap (the `_EDIT` family, `px-1`); every other row keeps
                      // `px-2`. Picked per row, never appended to a `px-2` string.
                      const td = rowEditing ? TD_EDIT : TD;
                      const tdText = rowEditing ? TD_TEXT_EDIT : TD_TEXT;
                      /**
                       * 🔀 **THE SHOOT-DATE CELL TITLE (2026-09-27) — the due
                       * sentence keeps its exact wording and GAINS the full
                       * date on a new line, rather than the two staying
                       * alternatives.** Owner ruling (controller decision,
                       * flagged): at Fit's 100px floor an overdue/due-soon
                       * `Sept` date loses its year, and until now the cell's
                       * `title` was the due sentence ALONE (the fallback chain
                       * below only reaches the plain date when there is NO due
                       * state at all) — so the year was unrecoverable by hover
                       * on exactly the rows the sentence already flags.
                       * `dueIndicatorTitle`'s sentence (via `shootDateTooltip`)
                       * is reused verbatim — never re-worded here — and the
                       * date line comes from the SAME `formatDate` the cell
                       * displays, so the two can never disagree. Computed once
                       * so the `<td>` below calls `shootDateTooltip` a single
                       * time rather than twice. Non-due and no-date cells are
                       * UNCHANGED (still the plain date, or no attribute).
                       */
                      const shootDueTitle = shootDateTooltip(gag);
                      const shootCellTitle = shootDueTitle
                        ? `${shootDueTitle}\n${formatDate(gag.shoot_date)}`
                        : (gag.shoot_date ? formatDate(gag.shoot_date) : undefined);
                      /**
                       * The control for one cell, or `null` to fall back to
                       * the read-only render.
                       *
                       * 🔑 Every call site reads `{editCell('x') || <ReadOnly/>}`,
                       * so a column with no control — RA, Scenes, Progress —
                       * and a column whose control is WITHHELD (Owner, from
                       * someone who may edit fields but not reassign) both
                       * degrade to exactly the cell that shipped before. **A
                       * withheld control renders nothing at all, never a
                       * disabled input**: a disabled control still advertises
                       * an action the server refuses.
                       *
                       * 🪤 `stopPropagation` is load-bearing, not defensive.
                       * The `<tr>` collapses the row on click, so without it
                       * every click into a `<select>` or `<input>` would close
                       * the editor the user was aiming at.
                       */
                      const editCell = (colKey) => {
                        if (!rowEditing) return null;
                        // 🪤 Ask the PREDICATE, never the element: a component
                        // that renders `null` is still a truthy object, so
                        // `{<GagRowEditCell/> || <ReadOnly/>}` would render an
                        // empty cell for every withheld control.
                        if (!hasGagRowControl(colKey, { canAssignOwner: canManage })) return null;
                        return (
                          <div onClick={(e) => e.stopPropagation()}>
                            <GagRowEditCell
                              columnKey={colKey}
                              gag={gag}
                              draft={editDraft}
                              onField={setEditField}
                              deptUsers={deptUsers || []}
                              subDepts={subDepts || []}
                              sceneSuggestions={sceneSuggestions || []}
                              canAssignOwner={canManage}
                              userId={userId}
                            />
                          </div>
                        );
                      };
                      return (
                        <React.Fragment key={rowKey}>
                      {/* Spacer ABOVE the expanded pair — !border-0 opts out
                          of the tbody divide-y line so the gap stays clean.
                          The mask's border-t is the list-above's CLOSING
                          bottom edge (the summary row's blue border-t opens
                          the island below the gap). */}
                      {expandedActive && (
                        <tr aria-hidden="true" className="!border-0">
                          <td colSpan={HEADERS.length} className="p-0">
                            <div className={spacerHeights}>
                              <div className={`${gapMask} border-t border-gray-200 dark:border-gray-700`} />
                            </div>
                          </td>
                        </tr>
                      )}
                      <tr
                        onClick={rowClick}
                        onKeyDown={(e) => rowEditKeyDown(e, rowKey, gag)}
                        title={rowTitle}
                        data-rowkey={rowKey}
                        // An OPEN row is an inline edit form: no table pan.
                        data-pan-skip={expandedActive ? '' : undefined}
                        // The page now scrolls the rows: a `?gag=` deep link's
                        // `scrollIntoView({ block: 'start' })` must stop BELOW
                        // the pinned toolbar + group head, not under them.
                        style={{ scrollMarginTop: rowScrollMarginTop }}
                        /**
                         * 🚨 **FOUR ROW STATES, AND THE FROZEN CELLS HAVE TO
                         * RENDER ALL FOUR THEMSELVES.** A sticky `<td>` is
                         * lifted out of the row and takes no background with
                         * it, so a frozen column is transparent by default and
                         * the rows underneath scroll visibly THROUGH it.
                         *   1. resting  — no `<tr>` fill at all; the group
                         *      card's own `bg-white dark:bg-gray-800` shows.
                         *      Restated on the cell by `ROW_BG_FROZEN_CELLS`.
                         *   2. hovered  — `ROW_HOVER` on the `<tr>`, mirrored
                         *      onto the cell by the same constant.
                         *   3. expanded (and expanded+hover) — `expandedFrame`
                         *      already writes its fill to `[&>td]`, i.e. to
                         *      EVERY cell including the frozen ones, so it
                         *      needs nothing added and must not be given
                         *      anything: a second background utility on one
                         *      element resolves by stylesheet order.
                         *   4. dimmed   — `opacity-40` on the `<tr>` makes the
                         *      whole row a single composited group, so the
                         *      frozen cell dims WITH its row and still covers
                         *      its own siblings. Nothing to restate.
                         * That is why the frozen rules ride the NON-EXPANDED
                         * branch of this ternary and nowhere else.
                         */
                        className={`transition-colors ${
                          isRowExpanded
                            ? expandedFrame
                            : `${ROW_HOVER} ${ROW_BG_FROZEN_CELLS}`
                        } ${rowClick ? 'cursor-pointer select-none' : ''} ${
                          // 🔀 2026-09-29: dimmed, NOT `pointer-events-none`
                          // (orders did the same 2026-09-28) — a click asks,
                          // then opens this row on Save or Discard.
                          dimmed ? 'opacity-40' : ''
                        }`}
                      >
                        {/* EXPAND — clip only. A CONTROL column (a chevron
                            button), no text to ellipse.
                            🔀 FROZEN since 2026-08-09, together with Title.
                            Product owner: *"will freezint the title column
                            help?"* — it does, and the chevron has to come with
                            it: it is the control that OPENS the row Title
                            names, so freezing Title alone would leave the
                            expander scrolling away from the row it expands.
                            `left: 0` and Title's offset is this column's LIVE
                            width; see `frozenGagColumnOffsets`. */}
                        <td
                          className={`${td} ${FROZEN_CELL_CLASS} sticky ${STICKY_COLUMN_CELL_Z}`}
                          style={{ left: frozenLeft.expand }}
                        >
                          <ExpandChevron
                            expanded={isRowExpanded}
                            onToggle={rowClick}
                          />
                        </td>
                        {/* ── The 13 data cells ──────────────────────────
                            Each reads `{editCell('key') || <readOnly/>}`. When
                            the row is not being edited — and for every column
                            that has no control, or whose control is withheld —
                            the fallback is byte-for-byte the cell that shipped
                            before inline editing existed. */}
                        {/* TITLE — TEXT: clip on the cell, ellipsis on the text
                            SPAN inside it (see the 2026-08-12 note below — this
                            line said "TEXT: ellipsis" while the ellipsis sat on
                            the `<td>`, and it moved). Already carried a `title`,
                            which is what makes the ellipsis honest. This is the
                            cell the product owner reported TWICE: at its 70px
                            floor the 15-char display string was painting 70.63px
                            into Status (measured, 2026-08-09), and the 15-char
                            cap itself then stopped the text growing when he
                            dragged the column WIDER (2026-08-12). */}
                        {/* 🔀 FROZEN since 2026-08-09 — the column the report
                            was about. It pins at the chevron column's LIVE
                            width, so dragging the chevron moves this offset
                            with it. Its editor is a plain `<input>`, which is
                            in-flow and so is unaffected by the frozen cell
                            being a stacking context — unlike the Scenes
                            picker's panel or the Actions kebab, neither of
                            which could live in a frozen column. */}
                        {/* 🔀 `TD_TEXT` → `TD` ON 2026-08-12, AND THAT IS A
                            DELIBERATE RE-DECLARATION RATHER THAN A LOSS. The
                            read-only branch below is now a FLEX CONTAINER, and
                            `text-overflow` acts on inline TEXT — so the cell's
                            own `text-ellipsis` went INERT the moment its child
                            became a flex box. This repo's rule is that an inert
                            declaration reads as a decision someone made, so it
                            is removed rather than left lying; the ellipsis now
                            lives on the span that actually holds the text.
                            §2 of `GagGroupTable.cellClipping.test.js` moves
                            `title` from ELLIPSES to CLIP_ONLY to match. */}
                        <td
                          className={`${td} font-medium text-gray-800 dark:text-gray-200 ${FROZEN_CELL_CLASS} sticky ${STICKY_COLUMN_CELL_Z}`}
                          style={{ left: frozenLeft.title }}
                          title={gag.title} aria-label={gag.title}>
                          {/* 🔻 THE 15-CHARACTER CAP IS GONE. It ran before the
                              cell's CSS, so the text could not follow the
                              column the user had just dragged wider — product
                              owner: *"if a user expands the table the text
                              should expand to show more content if available"*.
                              The title now truncates in CSS and grows with the
                              column. **Do not reintroduce `truncate(...)`
                              here**; see the helper's own docblock.
                              🔑 THE `title` ATTRIBUTE ABOVE STAYS. It is only
                              CONDITIONALLY redundant: at the column's 70px floor
                              the value is still hidden and hover is the only
                              channel left. `columnCellManifest.test.js` probes
                              it, too.
                              ⚠️ **The `editCell('title')` branch is NOT
                              wrapped, deliberately** — its inline `<input>` is
                              `w-full` and must stay a direct child of the cell;
                              inside a flex row it would size to its content. */}
                          {editCell('title') || (
                            <span className="flex items-center">
                              {/* 🪤 `min-w-0` is load-bearing: a flex item's
                                  default `min-width: auto` pins it at its
                                  intrinsic content width, so without it the
                                  span never shrinks and never ellipses. */}
                              <span className="truncate min-w-0">{gag.title}</span>
                              {/* £ budget-status glyph beside the title (sibling
                                  of the RA icon column) — shared with the card.
                                  Outside the truncating span so the ellipsis
                                  eats the TITLE, never the glyph; it carries its
                                  own `shrink-0`. */}
                              <BudgetGlyph gag={gag} />
                            </span>
                          )}
                        </td>
                        {/* STATUS — PILL: clip only, no ellipsis. The content is
                            an inline-flex badge, and `text-overflow` does not
                            act on one; the pill is clipped at the column edge
                            instead. No `title`: the value is a fixed vocabulary
                            and the pill is the label. */}
                        {/* 🔀 2026-09-27: its 80px floor (`Fit columns to
                            screen`) is narrower than the longest pills, so the
                            pill ellipses (`fit`) and the label is on `title` —
                            the "no title, the floor fits the vocabulary"
                            premise above no longer holds for this cell. */}
                        <td className={td} title={rowEditing ? undefined : (GAG_STATUS_LABELS[gag.status] || gag.status || undefined)}>
                          {editCell('status') || <StatusPill status={gag.status} fit />}
                        </td>
                        {/* RA — clip only: the content is an icon (or the muted
                            "N/A"), which already carries its own `title`. The
                            column is header-bound (`min === width`), so it can
                            only ever be widened. */}
                        <td className={`${td} text-center`}>
                          {/* RA requirement — compact icon (green tick / amber
                              tick / red cross / muted N/A) to keep the column
                              narrow; the detail panel keeps the fuller badge.
                              NOT inline-editable: `ra_status` is DERIVED from
                              linked risk assessments, not a column on `gags`. */}
                          {renderRaIcon(gag.ra_status)}
                        </td>
                        {/* SCENES — TEXT: ellipsis + its existing `title`. 🔀 The
                            `max-w-[140px] truncate` pair is GONE: `truncate`'s
                            three declarations now come from `TD_TEXT` (the cell
                            already carried `whitespace-nowrap`), and the
                            `max-w-` was **measured inert** — with the column
                            dragged to 400px the cell rendered at 400px against
                            its declared 140px cap, because `max-width` on a
                            table cell is left undefined by CSS 2.1 and Chromium
                            ignores it under `table-layout: fixed`. It was a
                            leftover from the auto-layout era, when the cell
                            really did size itself. */}
                        {/* 🪤 **AND IT STOPS CLIPPING WHILE THE ROW IS BEING
                            EDITED, WHICH IS A PRE-EXISTING BUG FOUND BY THIS
                            WORK RATHER THAN ONE IT CAUSED.** Scenes is the one
                            cell whose editor opens an IN-FLOW popup (the
                            `ui/Dropdown` panel inside `GagSceneMultiSelect`); the
                            panel is absolutely positioned against a `relative`
                            wrapper INSIDE this cell, so the cell is in its
                            containing-block chain and the cell's `overflow:
                            hidden` really does clip it — downwards, where the
                            panel opens. Measured: with the clip the first option
                            row hit-tested to the expanded panel behind it; with
                            `overflow: visible` on this one cell it hit-tested to
                            the option itself.
                            🔑 **This cell has carried `overflow: hidden` since
                            the inline-editing increment (`truncate` included
                            it), so the clipped picker shipped with that branch
                            — this change did not introduce it and does not
                            widen it.** The clip is what the READ-ONLY text needs
                            and is worth nothing in edit mode, where the control
                            is `w-full` and cannot overflow by construction.
                            ⚠️ The general rule for anyone adding another in-cell
                            popup: **`TD`'s clip and an in-flow popup are
                            incompatible — either portal the popup (which is what
                            keeps the Actions kebab working) or drop the clip
                            while the popup can exist.** */}
                        <td className={rowEditing ? TD_TEXT_UNCLIPPED_EDIT : TD_TEXT}
                          title={gag.scenes_display}>
                          {/* 🔀 Inline-editable since 2026-08-09, as a
                              MULTI-SELECT over the scene numbers already used
                              by gags in this department — there is no master
                              scene table, so harvested options are the only
                              possible options. The `PUT`'s separate
                              parse/replace path (`parseScenes` →
                              `replaceScenes`) is still the ONE writer; the
                              control just produces the comma-joined string it
                              already accepts. */}
                          {editCell('scenes') || (gag.scenes_display || '-')}
                        </td>
                        {/* Shoot date — 🔀 **THE TOOLTIP IS ON THE CELL, NOT THE
                            DOT (2026-08-08)**: owner, *"dot tool tips like the
                            amber dots have a tool tip but you have to hover over
                            the dot. please make it the whole cell that has the
                            same tool tip"*. `ShootDate` gets `titleOnParent` so
                            the dot stops setting its own — a child's `title`
                            SHADOWS its parent's, so keeping both is two tooltips,
                            which is the complaint. The string comes from
                            `dueIndicatorTitle` so the two cannot drift.
                            🔀 **MERGE 2026-08-09 — THREE branches wanted this one
                            attribute and each alone loses something, so the title
                            is a FALLBACK CHAIN rather than either side's value.**
                            Inline row editing added `editCell('shoot')`; the
                            whole-cell tooltip added `shootDateTooltip`; the
                            column-resize overflow fix added a plain formatted
                            date, because the cell is an inline-flex of DOT + date
                            that an ellipsis cannot act on, so a narrow column eats
                            the DATE outright.
                            🔑 Why the chain and not one or the other: a
                            comfortably-ahead gag has NO due state, so
                            `shootDateTooltip` correctly returns `undefined` and
                            renders no attribute — which is right until the column
                            is dragged narrow, at which point that gag's date is
                            clipped with nothing to recover it. The due tooltip
                            wins when there IS one (it says strictly more); the
                            formatted date covers the rest; `undefined` when there
                            is no date at all, so an empty `title=""` never renders
                            a blank popup.
                            🔀 **2026-09-27 — when there IS a due state, the
                            sentence no longer stands alone: the full date now
                            joins it on a second line** (`shootCellTitle`,
                            computed above), because at Fit's floor an
                            overdue/due-soon `Sept` date can lose its year and
                            the sentence alone was all that was left on hover. */}
                        <td
                          className={td}
                          title={shootCellTitle}
                        >
                          {editCell('shoot') || <ShootDate gag={gag} titleOnParent fit />}
                        </td>
                        {/* UNIT — TEXT: ellipsis, and a `title` ADDED. Free text
                            typed by the user, so the same rule as Sub-dept. */}
                        <td className={tdText} title={gag.unit || ''}>
                          {editCell('unit') || (gag.unit || '-')}
                        </td>
                        {/* D/N and INT/EXT — TEXT: ellipsis, but deliberately NO
                            `title`. Both render a label from a FIXED vocabulary
                            (`DAY_NIGHT_LABELS` / `INT_EXT_LABELS`), and both
                            floors already fit their longest label, so the
                            ellipsis is defence against a future label rather
                            than a live truncation hiding a value. Nothing is
                            recoverable-only-by-hover here. */}
                        <td className={tdText}>
                          {editCell('day_night') || (DAY_NIGHT_LABELS[gag.day_night] || '-')}
                        </td>
                        <td className={tdText}>
                          {editCell('int_ext') || (INT_EXT_LABELS[gag.int_ext] || '-')}
                        </td>
                        {/* LOCATION — TEXT: ellipsis + its existing `title`. Same
                            `max-w-[160px] truncate` removal as Scenes, and for
                            the same measured reason. */}
                        <td className={tdText} title={gag.location || ''}>
                          {editCell('location') || (gag.location || '-')}
                        </td>
                        {/* SUB-DEPT — TEXT: ellipsis, and a `title` ADDED with
                            it. A sub-department name is user-authored free text
                            ("Special Effects Workshop"), so clipping it without
                            a tooltip would hide a value with no way back.
                            🔀 Sits immediately BEFORE Owner since 2026-10-19
                            (owner: "changing one effects the options in the
                            other" — `gagOwnerOptions.js` couples the pair). */}
                        <td className={tdText}
                          title={gag.sub_department_name || ''}>
                          {editCell('sub_department') || (gag.sub_department_name || '-')}
                        </td>
                        {/* OWNER — clip only on the CELL: an inline-flex of the
                            lifecycle indicator plus the name, so an ellipsis on
                            the `<td>` cannot act on it and would be inert. The
                            ellipsis lives on the name span inside `OwnerLabel`,
                            which is the only arrangement that works here — see
                            that component's docblock. `OwnerLabel` also carries
                            the full name as its own `title`, so nothing is lost
                            to the clip. */}
                        <td className={td}>
                          {/* 🔻 `maxChars={9}` REMOVED 2026-08-12 — a character
                              cap cannot follow a dragged column width, so the
                              name now truncates in CSS and grows with the
                              column. The full name still drives the owner sort
                              and still sits in the tooltip.
                              ⚠️ The control is withheld from a caller who may
                              edit FIELDS but not REASSIGN — see GagRowEditCell.
                              Such a user sees an editable Status and a
                              read-only Owner in the same row, deliberately. */}
                          {/* 🔀 2026-09-29: while the row is edited, the
                              READ-ONLY owner shows the SHARED draft's owner —
                              a non-manager who takes/releases in the panel sees
                              the row agree before Save (owner's report). */}
                          {editCell('owner') || <OwnerLabel gag={rowEditing ? pendingOwnerGag(gag, editDraft, deptUsers) : gag} fit />}
                        </td>
                        {/* PRIORITY — PILL: clip only, same reasoning as Status.
                            Header-bound (`min === width`), so it only widens. */}
                        <td className={td} title={rowEditing ? undefined : (TASK_PRIORITY_LABELS[gag.priority] || gag.priority || undefined)}>
                          {editCell('priority') || <PriorityPill priority={gag.priority} fit />}
                        </td>
                        {/* PROGRESS — clip only, and an ellipsis here would be
                            actively wrong: the content is a fixed-width BAR plus
                            a d/t count, and a bar cannot ellipse. The bar keeps
                            its `min-w-[56px]`, so at the column's floor the
                            count clips before the bar does — the bar is the
                            signal, the count is the detail. */}
                        {/* 2026-09-27: at the 72px floor only the bar shows, so
                            the count it clips is on the cell's `title`. */}
                        <td className={td} title={gag.task_total > 0 ? `${gag.task_done}/${gag.task_total} tasks done` : undefined}>
                          <GagProgress gag={gag} barClassName="w-16 min-w-[56px]" />
                        </td>
                        {/* ACTIONS — clip only. A CONTROL column: the kebab is a
                            fixed-size button, so there is no text to ellipse.
                            🪤 Clipping this cell is safe ONLY because the kebab's
                            menu is PORTALLED out of the table (see the dropdown
                            note above) — an in-flow popup would be cut off by
                            this `overflow-hidden`. Verified in a browser, not
                            assumed. Action buttons must not toggle the row. */}
                        <td className={td} onClick={(e) => e.stopPropagation()}>{renderActions(gag)}</td>
                      </tr>

                      {/* Expanded detail panel — stays mounted through the
                          fade-out (closingKey) per the expand-to-edit pattern.
                          Blue bottom edge + shadow complete the lifted-card
                          frame started on the summary row (no side accents —
                          see the expandedFrame note). Skipped below md,
                          where the phone table hosts it — see isNarrow. */}
                      {/* 🔀 2026-10-25: the panel <tr> is the shared
                          `InlineEditPanelRow` (TableRowExpansion.js), moved
                          VERBATIM so the Manage Budgets tables render the same
                          one; `insetPx={2}` is the -2px card borders. */}
                      {expandedActive && !isNarrow && (
                        <InlineEditPanelRow
                          colSpan={HEADERS.length}
                          closing={closing}
                          fadeClass={fadeClass}
                          panelWidth={panelWidth}
                          insetPx={2}
                        >
                          {/* Row-edit Save/Discard bar — above the
                              panel, directly under the row it saves.
                              Inside the `sticky left-0` wrapper so it
                              stays reachable when the table is scrolled
                              right, exactly like the panel. */}
                          {renderRowEditBar(gag, rowKey)}
                          {renderPanel(gag, rowKey, { desktopRow: true })}
                        </InlineEditPanelRow>
                      )}
                      {/* Spacer BELOW the expanded pair — same lifecycle. No
                          border on this mask: the next row's own divide-y
                          border-t is the list's REOPENING top edge. When the
                          expanded gag is LAST in its group there is no next
                          row — the inner strip extends 4px past the gap to
                          swallow the card's stray bottom border/corner
                          remnant so the island doesn't sit above a floating
                          line. */}
                      {expandedActive && (
                        <tr aria-hidden="true" className="!border-0">
                          <td colSpan={HEADERS.length} className="p-0">
                            <div className={spacerHeights}>
                              <div className={gapMask}>
                                {isLastInGroup && (
                                  <div className="absolute left-0 right-0 top-full h-1 bg-gray-100 dark:bg-slate-800" />
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* ── PHONE TABLE (< md, 2026-10-03 prototype) — replaced the
                  card list. The SAME row models as the desktop table (key,
                  expansion, fade, dim, click + dirty guard) through the shared
                  `ui/PageScrollTable` shell and `TableRowExpansion` parts; the
                  expanded row hosts the SAME `GagDetailPanel` (`renderPanel`),
                  so nothing here decides who may edit. Mounted only below md
                  (`isNarrow`) — a second header pane at desktop widths would
                  be a hidden sticky element for nothing. In grouped views it
                  sits inside the group card, under the group title, and its
                  header pins just below that title. */}
              {isNarrow && (
                <div
                  data-testid={`gag-phone-table-${group.key}`}
                  className={`md:hidden [&>div]:border-0 ${flatMode ? '' : '[&>div]:rounded-t-none border-t border-gray-200 dark:border-gray-700'}`}
                >
                  <PageScrollTable
                    testIdPrefix={`gag-phone-${group.key}`}
                    columns={GAG_PHONE_TABLE_COLUMNS}
                    sortField={flatSort}
                    sortDir={sortDir}
                    onHeaderSort={onHeaderSort}
                    columnWidths={phoneColumnWidths}
                    stickyTop={flatMode ? groupHeaderTop : groupHeaderTop + (groupHeadHeight || 0)}
                    wrapRows={(_unused, phonePanelWidth) => rows.map((m) => renderPhoneRow(m, phonePanelWidth))}
                  >
                    {null}
                  </PageScrollTable>
                </div>
              )}
            </>
            )}
          </div>
        );
      })}
    </div>

    {/* THE ONE sticky-bottom bar for every group (md and up, and only while
        the table overflows a pane AND some group is open — a list of
        collapsed titles has nothing to scroll). Its spacer is 2px wider than
        the tables because the bar spans the cards' 1px borders and the panes
        do not; that makes its scroll RANGE equal theirs. */}
    {overflowsX && !isNarrow && (flatMode || groups.some((g) => expandedGroups[g.key])) ? (
      <StickyScrollbar
        data-page-scroll-pane
        contentStyle={{ minWidth: totalWidth + 2 }}
        testId="gag-table-scrollbar"
      />
    ) : null}

    {/*
      ══════════════════════════════════════════════════════════════════
      THE DIRTY-COLLAPSE PROMPT (2026-08-09)
      ══════════════════════════════════════════════════════════════════
      Product owner's decision, taken with the prototype's block-and-pulse in
      front of him. Every choice below is the ORDER pages' choice, copied
      verbatim so the same gesture means the same thing on both surfaces —
      which is the whole reason he asked for it.

      🔑 ORDER IS THE DESIGN, not an accident of the JSX. Left to right the
      labels escalate: change nothing → lose work → commit work, so the
      DESTRUCTIVE option is in the MIDDLE rather than beside the primary where
      cursor momentum lands. `ConfirmDialog` renders cancel · alt · confirm and
      already carries that reasoning.

      🔑 "Cancel" APPEARS NOWHERE. Beside "Discard changes" it reads as "cancel
      my edits" — the opposite of what it does.

      ⚠️ `initialFocus="cancel"` puts the keyboard on `Keep editing`, the only
      always-recoverable outcome, and `onCancel` is what BOTH Escape and an
      overlay click map to. So an instinctive Enter or Escape can neither lose
      the edit nor fire an unreviewed save.

      🚨 A FAILED SAVE CANNOT COLLAPSE THE ROW. `saveAndClose` hands
      `collapseRow` to `saveRowEdit`'s success callback; there is no `catch`
      here that has to remember.

      ONE dialog for the whole table, not one per row: single-expand means at
      most one row can ever be prompting.
    */}
    <ConfirmDialog
      open={collapsePromptKey != null}
      title="Unsaved changes to this gag"
      message="You have edited this gag and not saved it yet."
      {...UNSAVED_PROMPT_BUTTONS}
      variant="save"
      busy={savingRow}
      onCancel={keepEditing}
      onAlt={discardAndClose}
      onConfirm={saveAndClose}
    />
    {/* The row bar's Discard while a TASK edit is open (2026-10-18): the house
        deck's labels, two buttons — Keep editing takes focus (also Escape /
        overlay), Discard changes drops the gag draft AND the task edit. */}
    <ConfirmDialog
      open={discardConfirmKey != null}
      title="Discard unsaved changes?"
      message="This throws away your unsaved changes to this gag and to the task you are editing."
      cancelLabel={UNSAVED_PROMPT_BUTTONS.cancelLabel}
      confirmLabel={UNSAVED_PROMPT_BUTTONS.altLabel}
      initialFocus="cancel"
      variant="danger"
      onCancel={() => setDiscardConfirmKey(null)}
      onConfirm={discardEverything}
    />
    </div>
  );
}
